/**
 * Monkey Worlds — self-contained 2D platformer runtime hosted in a react-native-webview (native) /
 * srcDoc iframe (web). Canvas 2D only (no three.js) so the document stays small; everything —
 * engine, world-map scene, HUD, input, WebAudio bleeps — ships as one HTML string with zero
 * RN-bundle imports and zero network dependencies (always works offline).
 *
 * v2 movement kit: double jump, slide (half-height, passes hurdles), rope swings (pendulum with
 * momentum release), moving platforms (lifts), springs, jetpack pickups (fuel-limited thrust),
 * checkpoints with pit respawns, flying enemies. Art: per-world parallax stadium backdrops,
 * sport ground textures (pitch lines / parquet / infield dirt / yard lines), sport-themed enemy
 * skins and goal props, expressive canvas-drawn mascot with a run cycle. (spec AC-7/AC-8/AC-8b)
 *
 * Bridge protocol (window.ReactNativeWebView.postMessage, window.parent fallback; JSON):
 *   web → RN:  { type:'ready' }
 *              | { type:'level-clear', world, slot, stars, timeTicks, coins }
 *              | { type:'level-failed', world, slot }
 *              | { type:'world-complete', world }
 *              | { type:'load-error' }
 *   RN → web:  window.__monkeyWorldsApplyStart({ worldProgress, stars, insetTop })
 *
 * Gameplay predicates are EMBEDDED from monkeyWorldsScoring.ts via the EMBEDDED_FNS name-binding
 * table (minification-proof — see that file's header); level tilemaps come from
 * monkeyWorldsLevels.ts; world configs from monkeyWorlds.constants.ts (the internal sport ids are
 * deliberately NOT serialized so the runtime carries no league names — copy guard).
 *
 * Copy guard: no "bet" (pick/prediction wording), no real team/player/league/tournament names,
 * no "unlimited".
 */

import { MONKEY_WORLDS } from '../constants/monkeyWorlds.constants';
import { MONKEY_WORLDS_LEVELS } from './monkeyWorldsLevels';
import {
  MONKEY_WORLDS_MUSIC_DATA,
  MONKEY_WORLDS_ROOT_MIDI,
  getMonkeyWorldsMusicSource,
} from './monkeyWorldsMusic';
import {
  MONKEY_WORLDS_PHYSICS,
  MONKEY_WORLDS_SHOOTOUT,
  getMonkeyWorldsScoringSource,
} from './monkeyWorldsScoring';

/** `</script` inside embedded JSON/source would close the runtime's script tag early. */
function escapeScript(src: string): string {
  return src.replace(/<\/script/gi, '<\\/script');
}

export function buildMonkeyWorldsGameHtml(): string {
  // Copy-guard: strip the internal sport ids — the runtime only needs display + tuning fields.
  const worldsJson = JSON.stringify(
    MONKEY_WORLDS.map((w) => ({
      name: w.name,
      accent: w.accent,
      skyGradient: w.skyGradient,
      tileTint: w.tileTint,
      action: w.action,
    }))
  );
  const levelsJson = JSON.stringify(
    MONKEY_WORLDS_LEVELS.map((l) => ({
      world: l.world,
      slot: l.slot,
      timeParTicks: l.timeParTicks,
      boss: l.boss,
      rows: l.rows,
      platforms: l.platforms,
    }))
  );
  const physicsJson = JSON.stringify(MONKEY_WORLDS_PHYSICS);
  const scoringSource = getMonkeyWorldsScoringSource();
  const musicSource = getMonkeyWorldsMusicSource();
  const musicJson = JSON.stringify(MONKEY_WORLDS_MUSIC_DATA);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
<title>Monkey Worlds</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
  html,body { width:100%; height:100%; overflow:hidden; background:#0F0A1A; touch-action:none;
    font-family:-apple-system,system-ui,sans-serif; user-select:none; -webkit-user-select:none; }
  canvas { display:block; position:absolute; inset:0; }
</style>
</head>
<body>
<canvas id="c"></canvas>
<script>
window.__errs = [];
window.onerror = function(m, s, l, c, e){ window.__errs.push(String(m) + ' @' + l + ':' + c); };
// Bridge: native WebView first, srcDoc-iframe parent as the web fallback (same contract as siblings).
window.__post = function(m){
  var s = JSON.stringify(m);
  try { if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) { window.ReactNativeWebView.postMessage(s); return; } } catch (e) {}
  try { if (window.parent && window.parent !== window) window.parent.postMessage(s, '*'); } catch (e) {}
};
</script>
<script>
// Shootout rules the predicates below close over. Embedded functions cannot capture module
// scope, so it is injected here as a global with its canonical name before they are defined.
var MONKEY_WORLDS_SHOOTOUT = ${escapeScript(JSON.stringify(MONKEY_WORLDS_SHOOTOUT))};
// Root pitch for the score. The embedded timing fns below close over it, so — like the shootout
// rules — it is injected here as a global with its canonical name before they are defined.
var MONKEY_WORLDS_ROOT_MIDI = ${MONKEY_WORLDS_ROOT_MIDI};
// Pure gameplay predicates — embedded source from monkeyWorldsScoring.ts (name-bound, see header).
${escapeScript(scoringSource)}
// Score timing math — embedded source from monkeyWorldsMusic.ts (name-bound, same contract).
${escapeScript(musicSource)}
</script>
<script>
(function(){
  'use strict';
  try {
  var WORLDS = ${escapeScript(worldsJson)};
  var LEVELS = ${escapeScript(levelsJson)};
  var PHYS = ${escapeScript(physicsJson)};
  var MUSIC_DATA = ${escapeScript(musicJson)};
  var LEVELS_PER_WORLD = 6;

  // ---------- Canvas ----------
  var canvas = document.getElementById('c');
  var ctx = canvas.getContext('2d');
  var W = 0, H = 0, DPR = Math.min(2, window.devicePixelRatio || 1);
  var TILE = 32;
  var insetTop = 0;
  var HUD_LEFT = 18;
  var HUD_ROW2 = 64; // hearts/coins row — below the back button, the title AND the goal prompt
  var CONTROL_BAND = 18; // no buttons any more (AC-23) — just breathing room at the bottom edge
  /**
   * Tile scale is LEVEL-AWARE: fit the level's full height into the play area (above the control
   * band) while keeping at least ~8 tiles of width visible. A fixed scale left phones in portrait
   * showing 24 rows for a 14-row level — most of the screen was dead sky.
   */
  /**
   * Zoom (AC-25). Scale is chosen from how much of the level should be VISIBLE, not by squeezing
   * the whole level height on screen — the old rule fitted all ~14 rows into a 375px-tall
   * landscape stage, which worked out to ~32 tiles across and left everything tiny.
   * TILES_ACROSS is the dial; the level is then taller than the viewport, so a vertical camera
   * (updateCamera) does the rest.
   */
  var TILES_ACROSS = 15;
  var MIN_ROWS_VISIBLE = 7.2; // don't zoom so far that platforms overhead vanish
  function computeTile(gh){
    var playH = Math.max(120, H - CONTROL_BAND);
    var byWidth = W / TILES_ACROSS;
    var byHeight = playH / MIN_ROWS_VISIBLE;
    return Math.max(20, Math.floor(Math.min(byWidth, byHeight)));
  }
  /**
   * The game space is ALWAYS landscape (AC-24). The app is locked to portrait app-wide, so on
   * native the WebView never rotates with the device — instead the whole scene is drawn rotated
   * 90° onto the portrait canvas, and a phone held sideways shows an upright, full-bleed game.
   * On web the browser really does rotate, VW/VH swap, and no rotation is applied. Both routes
   * end at the same place, with no native module and no app-wide orientation change.
   */
  var VW = 0, VH = 0, ROT = false;
  function resize(){
    VW = window.innerWidth; VH = window.innerHeight;
    ROT = VH > VW;                      // portrait viewport → rotate the stage
    W = ROT ? VH : VW;                  // game space is landscape either way
    H = ROT ? VW : VH;
    canvas.width = Math.floor(VW * DPR); canvas.height = Math.floor(VH * DPR);
    canvas.style.width = VW + 'px'; canvas.style.height = VH + 'px';
    applyStageTransform();
    TILE = computeTile(game ? game.gh : 14);
  }
  function applyStageTransform(){
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    if (ROT) { ctx.translate(VW, 0); ctx.rotate(Math.PI / 2); }
  }
  /** Viewport point → game space. Must mirror applyStageTransform exactly. */
  function toStageX(px, py){ return ROT ? py : px; }
  function toStageY(px, py){ return ROT ? VW - px : py; }
  window.addEventListener('resize', resize);
  resize();

  // ---------- Boot data (from the host via applyStart) ----------
  var boot = { worldProgress: 0, stars: {}, applied: false };
  var sessionBytes = [0, 0, 0, 0];
  function furthestSlot(w){
    var server = unpackWorldProgress(boot.worldProgress)[w];
    return Math.max(server, sessionBytes[w]);
  }
  function starFor(w, s){ return boot.stars[w + ':' + s] || 0; }
  window.__monkeyWorldsApplyStart = function(payload){
    try {
      if (payload && typeof payload.worldProgress === 'number') boot.worldProgress = payload.worldProgress;
      if (payload && payload.stars && typeof payload.stars === 'object') boot.stars = payload.stars;
      if (payload && typeof payload.insetTop === 'number') insetTop = payload.insetTop;
      if (payload && typeof payload.muted === 'boolean') audioOn = !payload.muted;
      boot.applied = true;
    } catch (e) {}
  };

  // ---------- Audio: two buses, one-shot effects + the score ----------
  // Effects and music each get their own gain node so music can sit provably under the effects
  // (see the gains in monkeyWorldsMusic.ts) and so there is exactly one place to mute or duck.
  var audioOn = true, ac = null, sfxBus = null, musicBus = null;
  /**
   * The context is created lazily and resumed on every touch, because WebView audio contexts boot
   * suspended and only a real user gesture may start them — nothing is ever scheduled before
   * that, or it would be silently dropped. Failure is swallowed: the game stays playable, silent.
   */
  function audioCtx(){
    try {
      if (!ac) {
        ac = new (window.AudioContext || window.webkitAudioContext)();
        sfxBus = ac.createGain(); sfxBus.gain.value = 1; sfxBus.connect(ac.destination);
        musicBus = ac.createGain(); musicBus.gain.value = 0.9; musicBus.connect(ac.destination);
      }
      if (ac.state === 'suspended') ac.resume(); // never awaited — it throws on older engines
    } catch (e) { return null; }
    return ac;
  }
  function bleep(freq, dur, type, vol){
    if (!audioOn) return;
    var c = audioCtx(); if (!c) return;
    try {
      var o = c.createOscillator(), g = c.createGain();
      o.type = type || 'square'; o.frequency.value = freq;
      g.gain.setValueAtTime(vol || 0.08, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
      o.connect(g); g.connect(sfxBus);
      o.start(); o.stop(c.currentTime + dur);
    } catch (e) {}
  }
  var sfx = {
    jump: function(){ bleep(440, 0.12, 'square'); },
    doubleJump: function(){ bleep(560, 0.12, 'square'); },
    coin: function(){ bleep(1180, 0.09, 'triangle'); bleep(1560, 0.12, 'triangle'); },
    stomp: function(){ bleep(220, 0.1, 'square'); },
    hurt: function(){ bleep(110, 0.25, 'sawtooth'); },
    breakTile: function(){ bleep(180, 0.08, 'square'); },
    action: function(){ bleep(660, 0.1, 'square'); },
    spring: function(){ bleep(520, 0.08, 'triangle'); bleep(780, 0.14, 'triangle'); },
    rope: function(){ bleep(700, 0.07, 'triangle'); },
    jet: function(){ bleep(300, 0.05, 'sawtooth'); },
    checkpoint: function(){ bleep(880, 0.1, 'triangle'); bleep(1100, 0.14, 'triangle'); },
    slide: function(){ bleep(240, 0.09, 'square'); },
    clear: function(){ bleep(660, 0.12, 'triangle'); bleep(880, 0.14, 'triangle'); bleep(1320, 0.2, 'triangle'); },
    fail: function(){ bleep(200, 0.2, 'sawtooth'); bleep(140, 0.3, 'sawtooth'); },
  };


  // ---------- The score ----------
  /**
   * Lookahead scheduling, not a note-per-timer-tick loop: a 25 ms timer queues every note falling
   * in the next 120 ms against ac.currentTime, which is sample-accurate. The frame loop is not
   * usable as a clock here (rAF gets throttled — that is why this file already carries a watchdog),
   * and a bare timer would put every GC pause straight into the rhythm: at 132 bpm a 16th is 113 ms,
   * the same size as a bad timer hiccup. This absorbs a 60 ms stall inaudibly.
   */
  var MUSIC_LOOKAHEAD = 0.12, MUSIC_TICK_MS = 25;
  var music = { key: '', track: null, arr: null, notes: null, bass: null, bpm: 120, swing: 0,
                startedAt: 0, nextStep: 0, loopSteps: 0, loop: false, timer: null, scheduled: 0 };

  function musicNote(hz, when, durSec, wave, vol){
    if (!ac || !musicBus) return;
    try {
      var o = ac.createOscillator(), g = ac.createGain();
      o.type = wave; o.frequency.value = hz;
      // 8 ms attack — a hard setValueAtTime start clicks audibly on a continuous loop.
      g.gain.setValueAtTime(0.0001, when);
      g.gain.linearRampToValueAtTime(vol, when + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, when + durSec);
      o.connect(g); g.connect(musicBus);
      o.start(when); o.stop(when + durSec + 0.02);
      music.scheduled++;
    } catch (e) {}
  }

  function musicStop(){
    music.track = null; music.notes = null; music.key = '';
  }

  /** Resolve the scene's track over the world's arrangement and start it from the top. */
  function musicApplyScene(sceneId, worldIdx){
    if (!audioOn) { musicStop(); return; }
    var c = audioCtx(); if (!c) return;
    var t = MUSIC_DATA.scenes[sceneId];
    if (!t) { musicStop(); return; }
    var arr = MUSIC_DATA.arrangements[worldIdx] || MUSIC_DATA.arrangements[0];
    var spb = MUSIC_DATA.stepsPerBar;
    var notes = [];
    if (t.notes) {
      notes = t.notes;
      music.loopSteps = Math.max.apply(null, notes.map(function(n){ return n.t + n.d; }));
    } else {
      var from = t.bars[0] * spb, to = (t.bars[1] + 1) * spb;
      for (var i = 0; i < MUSIC_DATA.motif.length; i++) {
        var mn = MUSIC_DATA.motif[i];
        if (mn.t >= from && mn.t < to) notes.push({ t: mn.t - from, n: mn.n, d: mn.d });
      }
      music.loopSteps = to - from;
    }
    music.track = t; music.arr = arr; music.notes = notes;
    music.bass = MUSIC_DATA.bassPatterns[t.bass || arr.bass];
    music.barOffset = t.bars ? t.bars[0] : 0;
    music.bpm = (t.bpm || arr.bpm) * (t.tempoScale || 1);
    music.swing = t.bars ? arr.swing : 0;   // stingers are always straight
    music.loop = !!t.loop;
    music.nextStep = 0;
    // small pre-roll so the first note is never already in the past
    music.startedAt = c.currentTime + 0.06 + (t.delaySec || 0);
    if (!music.timer) music.timer = setInterval(musicSchedule, MUSIC_TICK_MS);
  }

  function musicSchedule(){
    if (!audioOn || !music.notes || !ac) return;
    var t = music.track, arr = music.arr, spb = MUSIC_DATA.stepsPerBar;
    var gain = t.gainScale || 1;
    // If we fell behind (backgrounded tab, long stall), resync instead of scheduling a backlog of
    // hundreds of oscillators in one tick.
    if (stepTime(music.nextStep, music.startedAt, music.bpm, music.swing) < ac.currentTime - 0.5) {
      music.startedAt = ac.currentTime - (stepTime(music.nextStep, 0, music.bpm, music.swing));
    }
    var guard = 0;
    while (guard++ < 256) {
      var when = stepTime(music.nextStep, music.startedAt, music.bpm, music.swing);
      if (when >= ac.currentTime + MUSIC_LOOKAHEAD) break;
      var step = music.loop ? music.nextStep % music.loopSteps : music.nextStep;
      if (!music.loop && step >= music.loopSteps) { musicStop(); return; }
      var sec = stepSec(music.bpm);
      // melody
      for (var i = 0; i < music.notes.length; i++) {
        var n = music.notes[i];
        if (n.t !== step) continue;
        var root = t.pedalRoot != null ? t.pedalRoot
          : (t.bars ? MUSIC_DATA.barRoots[(Math.floor(step / spb) + music.barOffset) % MUSIC_DATA.barRoots.length] : 0);
        var tr = (t.transpose != null ? t.transpose : arr.transpose);
        musicNote(noteHz(n.n + root + arr.melodyOctave, tr, MONKEY_WORLDS_ROOT_MIDI), when, n.d * sec * 0.92,
          t.melodyWave || arr.melodyWave, arr.melodyGain * gain);
      }
      // bass on every 8th
      if (music.bass && step % 2 === 0) {
        var slot = music.bass[(step / 2) % music.bass.length];
        if (slot != null) {
          var broot = t.pedalRoot != null ? t.pedalRoot
            : (t.bars ? MUSIC_DATA.barRoots[(Math.floor(step / spb) + music.barOffset) % MUSIC_DATA.barRoots.length] : 0);
          var btr = (t.transpose != null ? t.transpose : arr.transpose);
          musicNote(noteHz(slot + broot - 12, btr, MONKEY_WORLDS_ROOT_MIDI), when, sec * 1.7, arr.bassWave, arr.bassGain * gain);
        }
      }
      music.nextStep++;
    }
  }

  /** Mute is one switch for effects and score alike. */
  function setAudioOn(on){
    audioOn = !!on;
    if (!audioOn) { musicStop(); return; }
    audioCtx();
    music.key = ''; // force the scene poll to re-resolve and restart the track
  }

  /** Called from real gestures only — see audioCtx(). */
  function musicUnlock(){
    if (!audioOn) return;
    audioCtx();
  }

  window.__monkeyWorldsApplyAudio = function(payload){
    try { if (payload && typeof payload.muted === 'boolean') setAudioOn(!payload.muted); } catch (e) {}
  };

  document.addEventListener('visibilitychange', function(){
    try {
      if (document.hidden) { musicStop(); if (ac) ac.suspend(); }
      else if (audioOn && ac) { ac.resume(); music.key = ''; }
    } catch (e) {}
  });

  // ---------- Scene state ----------
  var scene = 'map'; // map | play | clear | failed | worldComplete
  var game = null;
  var lastClear = null;

  // ---------- Level parsing ----------
  function parseLevel(world, slot){
    var def = null;
    for (var i = 0; i < LEVELS.length; i++) if (LEVELS[i].world === world && LEVELS[i].slot === slot) def = LEVELS[i];
    if (!def) return null;
    var grid = [], coins = [], enemies = [], flyers = [], pitchers = [], ropes = [], springs = [], jets = [], checks = [];
    var spawn = { x: 2, y: 2 }, flag = { x: 4, y: 2 };
    for (var y = 0; y < def.rows.length; y++) {
      var row = def.rows[y].split('');
      for (var x = 0; x < row.length; x++) {
        var ch = row[x];
        if (ch === 'c') { coins.push({ x: x + 0.5, y: y + 0.5, got: false }); row[x] = ' '; }
        else if (ch === 'e') { enemies.push({ x: x + 0.5, y: y + 0.9, dir: -1, dead: false, stun: 0, charge: 0 }); row[x] = ' '; }
        else if (ch === 'f') { flyers.push({ x: x + 0.5, y: y + 0.5, bx: x + 0.5, by: y + 0.5, phase: (x * 7 + y) % 6, dead: false }); row[x] = ' '; }
        else if (ch === 'p') { pitchers.push({ x: x + 0.5, y: y + 0.9, cool: 90, dead: false }); row[x] = ' '; }
        else if (ch === 'R') { ropes.push({ x: x + 0.5, y: y + 0.5 }); row[x] = ' '; }
        else if (ch === 'T') { springs.push({ x: x + 0.5, y: y + 0.9, squash: 0 }); row[x] = ' '; }
        else if (ch === 'J') { jets.push({ x: x + 0.5, y: y + 0.5, got: false }); row[x] = ' '; }
        else if (ch === 'K') { checks.push({ x: x + 0.5, y: y + 0.9, lit: false }); row[x] = ' '; }
        else if (ch === 'S') { spawn = { x: x + 0.5, y: y + 0.9 }; row[x] = ' '; }
        else if (ch === 'F') { flag = { x: x + 0.5, y: y + 0.9 }; row[x] = ' '; }
      }
      grid.push(row);
    }
    var platforms = [];
    for (var pi = 0; pi < (def.platforms || []).length; pi++) {
      var pd = def.platforms[pi];
      platforms.push({ bx: pd.x, by: pd.y, w: pd.w, axis: pd.axis, range: pd.range, period: pd.period, x: pd.x, y: pd.y, px: pd.x, py: pd.y });
    }
    return {
      world: world, slot: slot, def: def, grid: grid, gw: grid[0].length, gh: grid.length,
      coins: coins, coinTotal: coins.length, enemies: enemies, flyers: flyers, pitchers: pitchers,
      ropes: ropes, springs: springs, jets: jets, checks: checks, platforms: platforms,
      spawn: spawn, flag: flag, projectiles: [], ball: null, particles: [],
      player: {
        x: spawn.x, y: spawn.y, vx: 0, vy: 0, w: 0.72, h: 0.92, facing: 1,
        grounded: false, sinceGround: 999, sincePress: 999, jumpHeld: false, airJumps: PHYS.AIR_JUMPS,
        cutArmed: false, hearts: PHYS.HEARTS, invuln: 0, tookDamage: false,
        swing: 0, actionCool: 0, actionHeld: false,
        sliding: 0, rope: null, ropeCooldown: 0, jetFuel: 0, onPlatform: -1,
      },
      ticks: 0, coinsGot: 0, camX: 0, camY: 0, anchorY: 0, over: false, scored: false, shots: 0, missFlash: 0,
      saveFlash: 0, saves: 0,
    };
  }

  function tileAt(g, x, y){
    if (x < 0 || y < 0 || x >= g.gw || y >= g.gh) return x < 0 || x >= g.gw ? '#' : ' ';
    return g.grid[Math.floor(y)][Math.floor(x)];
  }
  function isSolid(ch){ return ch === '#' || ch === 'B' || ch === 'W'; }
  function playerH(p){ return p.sliding > 0 ? PHYS.SLIDE_H : p.h; }

  // ---------- Input ----------
  var input = { left: false, right: false, jump: false, action: false, slide: false, moveX: 0 };
  var touches = {};
  /**
   * Tap zones are much larger than the drawn buttons: the lower-left quadrant is a two-half
   * steering pad, and the right cluster's zones are rectangles around each circle. Thumbs are
   * imprecise — the art is lean, the targets are not.
   */
  /**
   * Touch model (AC-23). No on-screen buttons at all — they ate the bottom of the playfield and
   * the arrow pads were the thing players disliked most. Instead:
   *   LEFT half  — an invisible steering pad. Press anywhere, then slide: the monkey runs in the
   *                direction of travel from your anchor. The anchor follows your thumb, so long
   *                drags never run out of room and a re-grip doesn't stall you.
   *   RIGHT half — gestures: tap = jump, swipe up = the sport shot, swipe down = slide.
   *
   * Crucially this tracks touchmove. The old scheme resolved a zone once on touchstart and never
   * again, so a thumb that drifted a few pixels kept the stale zone (or none) and the controls felt
   * dead — that was the "not responsive" report, not frame rate.
   */
  var STEER_DEAD = 7;        // px of slop before a drag counts as steering
  var STEER_FULL = 44;       // px of travel for full pace (analog in between)
  var STEER_REANCHOR = 46;   // keep the anchor trailing the thumb by at most this
  var TAP_SLOP = 18;         // px of movement still considered a tap
  var TAP_MS = 260;          // longer than this is no longer a tap
  var SWIPE_MIN = 26;        // px of vertical travel that commits a swipe

  function clearTouchInput(){
    input.left = input.right = input.jump = input.action = input.slide = false;
    input.moveX = 0;
    jumpHolds = 0;
  }
  /**
   * Fullscreen (AC-26). In a mobile browser the URL bar and system chrome eat roughly a third of
   * a landscape phone, which is what made the game look cut off. The Fullscreen API needs a user
   * gesture, so this is attempted on the first touch and then left alone. Being fullscreen is also
   * what makes Screen Orientation lock permissible on Android Chrome, so the handset can genuinely
   * rotate rather than relying on the in-canvas rotation. Every step is best-effort: a browser or
   * WebView that refuses simply keeps the rotated-stage path, which already works.
   */
  var fsTried = false;
  function tryFullscreen(){
    if (fsTried) return;
    fsTried = true;
    try {
      var el = document.documentElement;
      var req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen;
      if (!req) return;
      var p = req.call(el);
      if (p && p.then) {
        p.then(function(){
          try {
            if (screen.orientation && screen.orientation.lock) {
              var lp = screen.orientation.lock('landscape');
              if (lp && lp.catch) lp.catch(function(){});
            }
          } catch (e) {}
        }).catch(function(){});
      }
    } catch (e) {}
  }

  function beginTouch(id, x, y){
    if (x < W * 0.5) {
      touches[id] = { role: 'steer', ax: x, ay: y };
      return;
    }
    // Actions fire on PRESS, never on release. Waiting for lift-off (or for a gesture to be
    // recognised) puts the whole reaction time of the finger in front of every jump, which is
    // what made the controls feel laggy. Slide is still a swipe — it is the one action where a
    // few frames of recognition don't matter.
    var isShot = y < H * 0.34;         // upper band of the right side = the sport shot
    touches[id] = { role: 'act', kind: isShot ? 'shot' : 'jump', x0: x, y0: y, t0: nowMs(), swiped: false };
    if (isShot) {
      pulseAction();                   // one-shot: edge-triggered, fires on press
    } else {
      // Jump is HELD, not pulsed. The physics cuts the rise short the moment the control is
      // released (that is what makes jump height variable) — so a fixed 6-tick pulse chopped
      // every jump to ~2.1 tiles against a designed 5.3, and a 3-tile crate became unclearable.
      // Holding now gives the full arc; a quick tap still gives a deliberate short hop.
      jumpHolds++;
    }
  }
  function moveTouch(id, x, y){
    var t = touches[id];
    if (!t) return;
    if (t.role === 'steer') {
      var dx = x - t.ax;
      // analog: ramps from a standstill at the deadzone to full pace at STEER_FULL, so small
      // corrections are possible instead of the old all-or-nothing shove
      var mag = (Math.abs(dx) - STEER_DEAD) / (STEER_FULL - STEER_DEAD);
      if (mag < 0) mag = 0; else if (mag > 1) mag = 1;
      input.moveX = dx > 0 ? mag : -mag;
      input.right = input.moveX > 0;
      input.left = input.moveX < 0;
      // trail the anchor so the pad never "runs out" mid-level
      if (dx > STEER_REANCHOR) t.ax = x - STEER_REANCHOR;
      else if (dx < -STEER_REANCHOR) t.ax = x + STEER_REANCHOR;
      return;
    }
    // jump/shot already fired on press; a downward drag additionally asks for a slide
    if (t.swiped) return;
    if (y - t.y0 >= SWIPE_MIN) { pulseSlide(); t.swiped = true; }
  }
  function endTouch(id, x, y){
    var t = touches[id];
    delete touches[id];
    if (!t) return;
    if (t.role === 'steer') {
      // only drop steering if no other finger is still steering
      var stillSteering = false;
      for (var k in touches) if (touches[k] && touches[k].role === 'steer') stillSteering = true;
      if (!stillSteering) { input.left = input.right = false; input.moveX = 0; }
      return;
    }
    // the shot already fired on press; releasing the jump side ends the held jump, which is what
    // lets the physics cut the arc for a short hop
    if (t.role === 'act' && t.kind === 'jump' && jumpHolds > 0) jumpHolds--;
  }
  function nowMs(){ return (window.performance && performance.now) ? performance.now() : 0; }

  // Gestures are momentary, so hold the flag long enough for the fixed-step update to see it.
  var pulse = { jump: 0, action: 0, slide: 0 };
  var jumpHolds = 0;   // how many fingers are currently holding the jump side down
  function pulseJump(){ pulse.jump = 6; }
  function pulseAction(){ pulse.action = 6; }
  function pulseSlide(){ pulse.slide = 10; }
  var kb = { left: false, right: false, jump: false, action: false, slide: false };
  function drainPulses(){
    input.jump = pulse.jump > 0 || kb.jump || jumpHolds > 0;
    input.action = pulse.action > 0 || kb.action;
    input.slide = pulse.slide > 0 || kb.slide;
    if (kb.left) { input.left = true; input.moveX = -1; }
    if (kb.right) { input.right = true; input.moveX = 1; }
    if (pulse.jump > 0) pulse.jump--;
    if (pulse.action > 0) pulse.action--;
    if (pulse.slide > 0) pulse.slide--;
  }

  canvas.addEventListener('touchstart', function(ev){
    ev.preventDefault();
    musicUnlock();
    tryFullscreen();
    for (var i = 0; i < ev.changedTouches.length; i++) {
      var t = ev.changedTouches[i];
      var sx = toStageX(t.clientX, t.clientY), sy = toStageY(t.clientX, t.clientY);
      if (scene !== 'play') { handleTap(sx, sy); continue; }
      beginTouch(t.identifier, sx, sy);
    }
  }, { passive: false });
  canvas.addEventListener('touchmove', function(ev){
    ev.preventDefault();
    if (scene !== 'play') return;
    for (var i = 0; i < ev.changedTouches.length; i++) {
      var t = ev.changedTouches[i];
      moveTouch(t.identifier, toStageX(t.clientX, t.clientY), toStageY(t.clientX, t.clientY));
    }
  }, { passive: false });
  canvas.addEventListener('touchend', function(ev){
    ev.preventDefault();
    for (var i = 0; i < ev.changedTouches.length; i++) {
      var t = ev.changedTouches[i];
      endTouch(t.identifier, toStageX(t.clientX, t.clientY), toStageY(t.clientX, t.clientY));
    }
  }, { passive: false });
  canvas.addEventListener('touchcancel', function(ev){
    for (var i = 0; i < ev.changedTouches.length; i++) delete touches[ev.changedTouches[i].identifier];
    clearTouchInput();   // also zeroes jumpHolds
  });

  // Mouse mirrors the same model, so the web build and QA behave like a thumb.
  var mouseDown = null;
  canvas.addEventListener('mousedown', function(ev){
    musicUnlock();
    tryFullscreen();
    var mx = toStageX(ev.clientX, ev.clientY), my = toStageY(ev.clientX, ev.clientY);
    if (scene !== 'play') { handleTap(mx, my); return; }
    mouseDown = 'm';
    beginTouch('m', mx, my);
  });
  canvas.addEventListener('mousemove', function(ev){
    if (mouseDown) moveTouch('m', toStageX(ev.clientX, ev.clientY), toStageY(ev.clientX, ev.clientY));
  });
  canvas.addEventListener('mouseup', function(ev){
    if (mouseDown) { endTouch('m', toStageX(ev.clientX, ev.clientY), toStageY(ev.clientX, ev.clientY)); mouseDown = null; }
  });
  window.addEventListener('keydown', function(ev){
    if (ev.repeat) return;
    musicUnlock();
    if (ev.key === 'ArrowLeft' || ev.key === 'a') { kb.left = true; input.left = true; input.moveX = -1; }
    if (ev.key === 'ArrowRight' || ev.key === 'd') { kb.right = true; input.right = true; input.moveX = 1; }
    if (ev.key === 'z' || ev.key === ' ' || ev.key === 'ArrowUp' || ev.key === 'w') { kb.jump = true; if (scene !== 'play') handleTap(-1, -1); }
    if (ev.key === 'x' || ev.key === 'Shift') kb.action = true;
    if (ev.key === 's' || ev.key === 'ArrowDown') kb.slide = true;
  });
  window.addEventListener('keyup', function(ev){
    if (ev.key === 'ArrowLeft' || ev.key === 'a') { kb.left = false; input.left = false; if (input.moveX < 0) input.moveX = 0; }
    if (ev.key === 'ArrowRight' || ev.key === 'd') { kb.right = false; input.right = false; if (input.moveX > 0) input.moveX = 0; }
    if (ev.key === 'z' || ev.key === ' ' || ev.key === 'ArrowUp' || ev.key === 'w') kb.jump = false;
    if (ev.key === 'x' || ev.key === 'Shift') kb.action = false;
    if (ev.key === 's' || ev.key === 'ArrowDown') kb.slide = false;
  });

  var mapHits = [];
  var soundHit = { x: 0, y: 0 };
  function handleTap(x, y){
    if (Math.abs(x - soundHit.x) < 26 && Math.abs(y - soundHit.y) < 26) {
      setAudioOn(!audioOn);
      window.__post({ type: 'audio', muted: !audioOn });
      return;
    }
    if (scene === 'shootout') { shootoutTap(); return; }
    if (scene === 'map') {
      for (var i = 0; i < mapHits.length; i++) {
        var h = mapHits[i];
        if (x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h) {
          if (h.locked) { sfx.hurt(); return; }
          startLevel(h.world, h.slot);
          return;
        }
      }
    } else if (scene === 'clear' || scene === 'worldComplete') {
      var g = lastClear;
      if (scene === 'clear' && g && g.slot < LEVELS_PER_WORLD && y > H * 0.62 && y < H * 0.62 + 64) {
        startLevel(g.world, g.slot + 1);
      } else {
        scene = 'map';
      }
    } else if (scene === 'failed') {
      if (lastClear && y > H * 0.62 && y < H * 0.62 + 64) startLevel(lastClear.world, lastClear.slot);
      else scene = 'map';
    }
  }

  function startLevel(world, slot){
    game = parseLevel(world, slot);
    if (!game) return;
    TILE = computeTile(game.gh);
    // Drop any finished shootout. It used to survive into the next level still holding
    // phase='result'/outcome='goal', so updateShootout completed the new level on its first tick
    // and the whole world chain-cleared from a single win.
    SO = null;
    scene = 'play';
    sfx.action();
  }

  // ---------- Combat / verbs ----------
  function hurtPlayer(p){
    if (p.invuln > 0) return;
    p.hearts -= 1; p.tookDamage = true; p.invuln = PHYS.INVULN_TICKS;
    p.vy = -0.12; sfx.hurt();
    if (p.hearts <= 0) endLevel(false);
  }
  /**
   * Costs a heart and returns the player to the last checkpoint. Used for BOTH falling out of the
   * world and touching spikes — spike basins have solid floors, so without a respawn the player
   * lands in one and is trapped there draining hearts with no way out.
   */
  function loseHeartAndRespawn(g){
    var p = g.player;
    p.tookDamage = true;
    p.hearts -= 1;
    sfx.hurt();
    if (p.hearts <= 0) { endLevel(false); return; }
    var lit = [];
    for (var i = 0; i < g.checks.length; i++) if (g.checks[i].lit) lit.push(g.checks[i]);
    var rp = respawnPointFor(lit, g.spawn, p.x);
    p.x = rp.x; p.y = rp.y; p.vx = 0; p.vy = 0;
    p.rope = null; p.sliding = 0;
    p.invuln = PHYS.INVULN_TICKS;
    g.camX = Math.max(0, p.x - (W / TILE) / 2);
  }
  function breakTileAt(g, tx, ty){
    if (ty < 0 || ty >= g.gh || tx < 0 || tx >= g.gw) return;
    g.grid[ty][tx] = ' ';
    for (var i = 0; i < 6; i++) g.particles.push({ x: tx + 0.5, y: ty + 0.5, vx: (Math.random() - 0.5) * 0.2, vy: -Math.random() * 0.2, life: 24, color: '#A9743B' });
    sfx.breakTile();
  }
  function puff(g, x, y, color, n){
    for (var i = 0; i < n; i++) g.particles.push({ x: x, y: y, vx: (Math.random() - 0.5) * 0.12, vy: -Math.random() * 0.08, life: 18, color: color });
  }
  /**
   * The sport shot — the same button in every world, but it plays that world's sport: kick a
   * drive, shoot an arcing jumper, bat a line drive, throw a pass. Only a short cooldown gates it
   * — you can always retry, so a level can never soft-lock on a missed attempt.
   */
  function doShot(g, power){
    var p = g.player;
    var act = WORLDS[g.world].action;
    if (p.actionCool > 0) return;
    p.actionCool = act.cooldownTicks;
    // the bat also swings an arc — it reflects pitches and clears enemies
    if (act.type === 'bat') p.swing = PHYS.SWING_ARC_TICKS;
    var scale = shotSpeedScale(power);
    g.ball = {
      x: p.x + p.facing * 0.55, y: p.y - 0.5,
      vx: p.facing * act.shot.vx * scale, vy: act.shot.vy * scale,
      g: act.shot.g, life: PHYS.BALL_LIFE_TICKS, spin: 0,
    };
    g.shots++;
    sfx.action();
  }

  // ---------- Level end ----------
  function endLevel(won){
    var g = game;
    if (!g || g.over) return;
    g.over = true;
    if (won) {
      var stars = levelStars(g.ticks, g.def.timeParTicks, g.coinsGot, g.coinTotal, g.player.tookDamage, g.shots, MONKEY_WORLDS_SHOOTOUT.STAR_SHOTS);
      sessionBytes[g.world] = Math.max(sessionBytes[g.world], g.slot);
      var key = g.world + ':' + g.slot;
      if (stars > (boot.stars[key] || 0)) boot.stars[key] = stars;
      lastClear = { world: g.world, slot: g.slot, stars: stars, ticks: g.ticks, coins: g.coinsGot, coinTotal: g.coinTotal };
      window.__post({ type: 'level-clear', world: g.world, slot: g.slot, stars: stars, timeTicks: g.ticks, coins: g.coinsGot });
      sfx.clear();
      if (g.slot === LEVELS_PER_WORLD) {
        window.__post({ type: 'world-complete', world: g.world });
        scene = 'worldComplete';
      } else {
        scene = 'clear';
      }
    } else {
      lastClear = { world: g.world, slot: g.slot };
      window.__post({ type: 'level-failed', world: g.world, slot: g.slot });
      sfx.fail();
      scene = 'failed';
    }
  }

  // ---------- Update (fixed 60 Hz) ----------
  function update(){
    if (scene !== 'play' || !game) return;
    var g = game, p = g.player;
    var act = WORLDS[g.world].action;
    g.ticks++;

    if (p.actionCool > 0) p.actionCool--;
    if (p.invuln > 0) p.invuln--;
    if (p.swing > 0) p.swing--;
    if (p.ropeCooldown > 0) p.ropeCooldown--;

    // --- moving platforms (deterministic paths) ---
    for (var mp = 0; mp < g.platforms.length; mp++) {
      var pl = g.platforms[mp];
      pl.px = pl.x; pl.py = pl.y;
      if (pl.axis === 'x') { pl.x = platformPosAt(pl.bx, pl.range, pl.period, g.ticks); pl.y = pl.by; }
      else { pl.y = platformPosAt(pl.by, pl.range, pl.period, g.ticks); pl.x = pl.bx; }
    }

    // --- rope swing (pendulum overrides regular physics) ---
    if (p.rope) {
      var rp = p.rope;
      var pump = (input.right ? 1 : 0) - (input.left ? 1 : 0);
      var angAcc = -(PHYS.GRAVITY * 1.35 / rp.len) * Math.sin(rp.angle) + pump * PHYS.ROPE_PUMP;
      rp.angVel = (rp.angVel + angAcc) * PHYS.ROPE_DAMP;
      rp.angle += rp.angVel;
      p.x = rp.ax + Math.sin(rp.angle) * rp.len;
      p.y = rp.ay + Math.cos(rp.angle) * rp.len;
      p.facing = rp.angVel >= 0 ? 1 : -1;
      // Jump-edge bookkeeping MUST run here too — the rope branch returns early, so leaving it to
      // the main path would strand jumpHeld=true forever and make the rope impossible to let go of.
      var ropeJumpPressed = input.jump && !p.jumpHeld;
      if (input.jump) p.jumpHeld = true; else p.jumpHeld = false;
      if (ropeJumpPressed || input.slide) {
        var rel = ropeReleaseVelocity(rp.angle, rp.angVel, rp.len);
        // a jump release adds lift; a slide release just drops you off
        p.vx = rel.vx; p.vy = ropeJumpPressed ? rel.vy - 0.12 : rel.vy;
        p.rope = null; p.ropeCooldown = PHYS.ROPE_REGRAB_TICKS;
        p.airJumps = PHYS.AIR_JUMPS; p.cutArmed = false;
        sfx.jump();
      }
      // hazards still apply while swinging
      collideHazards(g, p);
      updateWorldEntities(g, p, act);
      updateCamera(g, p);
      return;
    }

    // --- slide ---
    if (p.sliding > 0) {
      p.sliding--;
      var wantStand = p.sliding === 0 || (!input.slide && p.sliding < PHYS.SLIDE_TICKS - 8);
      if (wantStand) {
        var headTile = tileAt(g, Math.floor(p.x), Math.floor(p.y - p.h));
        if (slideCanStand(headTile)) p.sliding = 0;
        else p.sliding = Math.max(p.sliding, 4); // stay low until there's headroom
      }
    } else if (input.slide && p.grounded && Math.abs(p.vx) > 0.02) {
      p.sliding = PHYS.SLIDE_TICKS;
      p.vx = p.facing * PHYS.RUN_MAX * PHYS.SLIDE_BOOST;
      puff(g, p.x, p.y, '#9CA3AF', 4);
      sfx.slide();
    }

    // --- horizontal intent ---
    // analog when steering by thumb, ±1 for keys (see input.moveX)
    var move = input.moveX || ((input.right ? 1 : 0) - (input.left ? 1 : 0));
    if (move !== 0 && p.sliding === 0) p.facing = move > 0 ? 1 : -1;
    var accel = PHYS.RUN_ACCEL * (p.grounded ? 1 : PHYS.AIR_CONTROL);
    if (p.sliding > 0) {
      p.vx *= 0.985; // slide friction — a long carpet glide
    } else if (move !== 0) {
      p.vx += move * accel;
      var cap = PHYS.RUN_MAX * Math.min(1, Math.abs(move));
      if (p.vx > cap) p.vx = cap;
      if (p.vx < -cap) p.vx = -cap;
    } else {
      p.vx *= p.grounded ? 0.78 : 0.94;
      if (Math.abs(p.vx) < 0.002) p.vx = 0;
    }

    // --- sport shot: edge-triggered, full power ---
    // In-play shots are a traversal tool (break blocks, stun enemies), and the control is now a
    // swipe — which cannot be "held", so the old hold-to-charge model left every shot at ~11%
    // power and unable to reach anything. Aiming lives in the shootout, which has its own meter.
    if (input.action && !p.actionHeld) doShot(g, 1);
    p.actionHeld = input.action;

    // --- jump: coyote + buffer, then the double jump ---
    p.sinceGround = p.grounded ? 0 : p.sinceGround + 1;
    var jumpPressed = input.jump && !p.jumpHeld;
    if (jumpPressed) { p.sincePress = 0; p.jumpHeld = true; }
    else if (!input.jump) { p.jumpHeld = false; p.sincePress++; }
    else p.sincePress++;
    if (coyoteJumpAllowed(p.sinceGround, p.sincePress, PHYS.COYOTE_TICKS, PHYS.BUFFER_TICKS) && p.vy >= -0.001) {
      p.vy = PHYS.JUMP_VEL; p.grounded = false; p.sinceGround = 99; p.sincePress = 99;
      p.sliding = 0; p.cutArmed = true; sfx.jump();
    } else if (jumpPressed && airJumpAllowed(p.airJumps, p.grounded, p.sinceGround, PHYS.COYOTE_TICKS)) {
      p.airJumps--; p.vy = PHYS.JUMP_VEL * 0.95; p.cutArmed = true;
      puff(g, p.x, p.y, '#FFFFFF', 6);
      sfx.doubleJump();
    }
    // Variable jump height applies ONLY to a jump the player initiated. Without cutArmed this also
    // ate spring/rope/jetpack launches every frame the button was up — which is what made the
    // trampolines feel like nothing.
    if (p.cutArmed && !input.jump && p.vy < 0 && p.jetFuel === 0) p.vy *= PHYS.JUMP_CUT;
    if (p.vy >= 0) p.cutArmed = false;

    // --- jetpack thrust ---
    if (p.jetFuel > 0 && input.jump) {
      p.vy -= PHYS.JET_THRUST;
      if (p.vy < -PHYS.JET_MAX_RISE) p.vy = -PHYS.JET_MAX_RISE;
      p.jetFuel--;
      if (g.ticks % 3 === 0) { g.particles.push({ x: p.x - p.facing * 0.2, y: p.y - 0.1, vx: -p.facing * 0.03, vy: 0.06, life: 14, color: '#FB923C' }); sfx.jet(); }
    }

    // --- gravity ---
    p.vy += PHYS.GRAVITY;
    if (p.vy > PHYS.MAX_FALL) p.vy = PHYS.MAX_FALL;

    // --- integrate X ---
    p.x += p.vx;
    var ph = playerH(p);
    var top = Math.floor(p.y - ph), bottom = Math.floor(p.y - 0.01);
    for (var ty = top; ty <= bottom; ty++) {
      var txEdge = p.vx > 0 ? Math.floor(p.x + p.w / 2) : Math.floor(p.x - p.w / 2);
      var ch = tileAt(g, txEdge, ty);
      var blocked = isSolid(ch) || (ch === 'h' && hurdleBlocks(p.sliding > 0));
      if (blocked) {
        p.x = resolveAxis(p.x - p.w / 2, p.w, p.vx, txEdge, 1) + p.w / 2;
        p.vx = 0;
      }
    }

    // --- integrate Y ---
    var prevFeet = p.y;
    p.y += p.vy;
    p.grounded = false;
    p.onPlatform = -1;
    var left = Math.floor(p.x - p.w / 2 + 0.02), right = Math.floor(p.x + p.w / 2 - 0.02);
    if (p.vy >= 0) {
      var feetRow = Math.floor(p.y);
      for (var tx = left; tx <= right; tx++) {
        var chF = tileAt(g, tx, feetRow);
        var oneWay = chF === '=' && prevFeet <= feetRow + 0.05;
        if (isSolid(chF) || oneWay) {
          p.y = feetRow; p.vy = 0; p.grounded = true; p.airJumps = PHYS.AIR_JUMPS; p.cutArmed = false;
          break;
        }
      }
      // moving platforms: land on top band, get carried
      if (!p.grounded) {
        for (var lp = 0; lp < g.platforms.length; lp++) {
          var plat = g.platforms[lp];
          var withinX = p.x + p.w / 2 > plat.x && p.x - p.w / 2 < plat.x + plat.w;
          if (withinX && prevFeet <= plat.py + 0.3 && p.y >= plat.y - 0.05 && p.y <= plat.y + 0.5) {
            p.y = plat.y; p.vy = 0; p.grounded = true; p.airJumps = PHYS.AIR_JUMPS;
            p.cutArmed = false; p.onPlatform = lp;
            break;
          }
        }
      }
    } else {
      var headRow = Math.floor(p.y - ph);
      for (var tx2 = left; tx2 <= right; tx2++) {
        var chH = tileAt(g, tx2, headRow);
        if (isSolid(chH)) {
          if (chH === 'B') { breakTileAt(g, tx2, headRow); continue; }
          p.y = (headRow + 1) + ph; p.vy = 0;
          break;
        }
      }
    }
    // carry with the platform under our feet
    if (p.onPlatform >= 0) {
      var carrier = g.platforms[p.onPlatform];
      p.x += carrier.x - carrier.px;
      p.y += carrier.y - carrier.py;
    }

    // --- springs ---
    for (var si = 0; si < g.springs.length; si++) {
      var sp = g.springs[si];
      if (sp.squash > 0) sp.squash--;
      if (p.vy >= 0 && aabbOverlap(p.x - p.w / 2, p.y - 0.2, p.w, 0.25, sp.x - 0.45, sp.y - 0.5, 0.9, 0.5)) {
        p.vy = PHYS.SPRING_VY; p.grounded = false; p.airJumps = PHYS.AIR_JUMPS;
        p.cutArmed = false; p.sliding = 0; sp.squash = 12;
        puff(g, sp.x, sp.y - 0.4, WORLDS[g.world].accent, 6);
        sfx.spring();
      }
    }

    // --- rope grab ---
    if (!p.rope && p.ropeCooldown === 0 && !p.grounded) {
      for (var ri = 0; ri < g.ropes.length; ri++) {
        var ra = g.ropes[ri];
        var dx = p.x - ra.x, dy = p.y - ra.y;
        if (Math.abs(dx) < 0.9 && dy > 1.2 && dy < PHYS.ROPE_LEN + 0.4) {
          var len = Math.min(PHYS.ROPE_LEN, Math.max(2, Math.sqrt(dx * dx + dy * dy)));
          var angle = Math.atan2(dx, dy);
          // project current velocity onto the tangent so momentum carries into the swing
          var tvx = Math.cos(angle), tvy = -Math.sin(angle);
          var angVel = (p.vx * tvx + p.vy * tvy) / len;
          p.rope = { ax: ra.x, ay: ra.y, len: len, angle: angle, angVel: angVel };
          p.sliding = 0; p.airJumps = PHYS.AIR_JUMPS; p.cutArmed = false;
          // consume the in-flight press so the grab frame itself can't instantly release
          p.jumpHeld = input.jump;
          sfx.rope();
          break;
        }
      }
    }

    // --- jetpack pickups ---
    for (var ji = 0; ji < g.jets.length; ji++) {
      var jp = g.jets[ji];
      if (!jp.got && aabbOverlap(p.x - p.w / 2, p.y - ph, p.w, ph, jp.x - 0.4, jp.y - 0.4, 0.8, 0.8)) {
        jp.got = true; p.jetFuel = PHYS.JET_FUEL_TICKS;
        puff(g, jp.x, jp.y, '#FB923C', 8);
        sfx.checkpoint();
      }
    }

    // --- checkpoints ---
    for (var ki = 0; ki < g.checks.length; ki++) {
      var kc = g.checks[ki];
      // full-column trigger — a tight box let you jump clean over a checkpoint and then respawn
      // all the way back at the start
      if (!kc.lit && Math.abs(p.x - kc.x) < 0.95 && p.y > kc.y - 4 && p.y < kc.y + 1.2) {
        kc.lit = true;
        puff(g, kc.x, kc.y - 1, WORLDS[g.world].accent, 8);
        sfx.checkpoint();
      }
    }

    collideHazards(g, p);

    // --- fell out → checkpoint respawn (a heart) or fail (no hearts left) ---
    if (p.y > g.gh + 3) { loseHeartAndRespawn(g); if (g.over) return; }

    updateWorldEntities(g, p, act);

    // --- coins ---
    for (var ci = 0; ci < g.coins.length; ci++) {
      var c = g.coins[ci];
      if (!c.got && aabbOverlap(p.x - p.w / 2, p.y - ph, p.w, ph, c.x - 0.35, c.y - 0.35, 0.7, 0.7)) {
        c.got = true; g.coinsGot++; sfx.coin();
      }
    }

    // --- particles ---
    for (var pa = g.particles.length - 1; pa >= 0; pa--) {
      var part = g.particles[pa];
      part.vy += PHYS.GRAVITY * 0.6; part.x += part.vx; part.y += part.vy; part.life--;
      if (part.life <= 0) g.particles.splice(pa, 1);
    }

    // Crossing the finish line hands over to the shootout — the level's sport climax. Deliberately
    // an x-only test at any height: a box could be (and was) jumped clean over.
    if (p.x + p.w / 2 >= g.flag.x - 0.4) {
      startShootout(g);
      return;
    }

    updateCamera(g, p);
  }

  function collideHazards(g, p){
    if (p.invuln > 0) return;
    var left = Math.floor(p.x - p.w / 2 + 0.02), right = Math.floor(p.x + p.w / 2 - 0.02);
    for (var sx = left; sx <= right; sx++) {
      if (tileAt(g, sx, Math.floor(p.y - 0.05)) === '^') { loseHeartAndRespawn(g); return; }
    }
  }

  function updateWorldEntities(g, p, act){
    var ph = playerH(p);

    // the shot ball — bounces and rolls so a near-miss still travels on toward the goal
    if (g.ball) {
      var b = g.ball;
      b.life--;
      b.spin += b.vx * 0.6;
      b.vy += (b.g || PHYS.BALL_GRAVITY); b.x += b.vx; b.y += b.vy;
      var btx = Math.floor(b.x), bty = Math.floor(b.y);
      var bch = tileAt(g, btx, bty);
      if (kickBreaksTile(bch) || ballBreaksWall(bch)) { breakTileAt(g, btx, bty); g.ball = null; }
      else if (isSolid(bch)) {
        // resolve against the surface we actually hit, then rebound
        var fromAbove = b.vy > 0 && !isSolid(tileAt(g, btx, Math.floor(b.y - b.vy)));
        if (fromAbove) { b.y = bty - 0.01; b.vy = -Math.abs(b.vy) * PHYS.BALL_BOUNCE; b.vx *= 0.94; }
        else { b.vx = -b.vx * 0.4; b.x += b.vx; }
        if (Math.abs(b.vy) < 0.02 && Math.abs(b.vx) < 0.02) g.ball = null;
      }
      if (g.ball && (b.life <= 0 || b.x < 0 || b.x > g.gw || b.y > g.gh + 2)) g.ball = null;
      if (g.ball) {
        for (var ei0 = 0; ei0 < g.enemies.length; ei0++) {
          var en0 = g.enemies[ei0];
          if (!en0.dead && aabbOverlap(b.x - 0.2, b.y - 0.2, 0.4, 0.4, en0.x - 0.4, en0.y - 0.8, 0.8, 0.8)) {
            en0.stun = PHYS.STUN_TICKS; g.ball = null; sfx.stomp(); break;
          }
        }
      }
      if (g.ball) {
        for (var fi0 = 0; fi0 < g.flyers.length; fi0++) {
          var fl0 = g.flyers[fi0];
          if (!fl0.dead && aabbOverlap(b.x - 0.2, b.y - 0.2, 0.4, 0.4, fl0.x - 0.4, fl0.y - 0.4, 0.8, 0.8)) {
            fl0.dead = true; g.ball = null; sfx.stomp(); break;
          }
        }
      }
    }

    // patrol enemies
    for (var i = 0; i < g.enemies.length; i++) {
      var e = g.enemies[i];
      if (e.dead) continue;
      if (e.stun > 0) { e.stun--; e.charge = 0; }
      else {
        if (e.charge > 0) e.charge--;
        else if (enemyShouldCharge(p.x - e.x, p.y - e.y, e.dir, g.slot)) {
          e.charge = PHYS.ENEMY_CHARGE_TICKS;
          puff(g, e.x, e.y - 0.4, '#F87171', 4);
        }
        var speed = e.charge > 0 ? PHYS.ENEMY_CHARGE_SPEED : PHYS.ENEMY_SPEED;
        var aheadX = e.x + e.dir * 0.5;
        var wallCh = tileAt(g, Math.floor(aheadX), Math.floor(e.y - 0.4));
        var floorCh = tileAt(g, Math.floor(aheadX), Math.floor(e.y + 0.1));
        if (isSolid(wallCh) || wallCh === 'h' || (!isSolid(floorCh) && floorCh !== '=')) {
          e.dir = -e.dir; e.charge = 0;
        } else e.x += e.dir * speed;
      }
      if (aabbOverlap(p.x - p.w / 2, p.y - ph, p.w, ph, e.x - 0.4, e.y - 0.8, 0.8, 0.8)) {
        var outcome = playerEnemyOutcome(p.vy, p.y, e.y - 0.8, e.stun > 0, p.invuln > 0);
        if (outcome === 'stomp') { e.dead = true; p.vy = PHYS.JUMP_VEL * 0.6; p.airJumps = PHYS.AIR_JUMPS; sfx.stomp(); }
        else if (outcome === 'hurt') { if (p.sliding > 0) { e.stun = PHYS.STUN_TICKS; sfx.stomp(); } else hurtPlayer(p); }
        else if (p.swing > 0) { e.dead = true; sfx.stomp(); }
      }
      if (p.swing > 0 && aabbOverlap(p.x + p.facing * 0.7 - 0.6, p.y - 1.2, 1.2, 1.4, e.x - 0.4, e.y - 0.8, 0.8, 0.8)) {
        e.dead = true; sfx.stomp();
      }
    }

    // flyers (sine hover)
    for (var fi = 0; fi < g.flyers.length; fi++) {
      var fl = g.flyers[fi];
      if (fl.dead) continue;
      // drift toward the player's height when they're near — flyers now actually hunt
      if (Math.abs(p.x - fl.bx) < 9) fl.by += (p.y - 0.5 - fl.by) * 0.004;
      fl.y = fl.by + Math.sin(g.ticks * 0.06 + fl.phase) * 1.2;
      fl.x = fl.bx + Math.sin(g.ticks * 0.022 + fl.phase) * 1.8;
      if (aabbOverlap(p.x - p.w / 2, p.y - ph, p.w, ph, fl.x - 0.38, fl.y - 0.38, 0.76, 0.76)) {
        var fOut = playerEnemyOutcome(p.vy, p.y, fl.y - 0.38, false, p.invuln > 0);
        if (fOut === 'stomp') { fl.dead = true; p.vy = PHYS.JUMP_VEL * 0.6; p.airJumps = PHYS.AIR_JUMPS; sfx.stomp(); }
        else if (fOut === 'hurt') hurtPlayer(p);
        else if (p.swing > 0) { fl.dead = true; sfx.stomp(); }
      }
      if (p.swing > 0 && aabbOverlap(p.x + p.facing * 0.7 - 0.6, p.y - 1.2, 1.2, 1.4, fl.x - 0.38, fl.y - 0.38, 0.76, 0.76)) {
        fl.dead = true; sfx.stomp();
      }
    }

    // pitchers + projectiles
    for (var pi = 0; pi < g.pitchers.length; pi++) {
      var pit = g.pitchers[pi];
      if (pit.dead) continue;
      pit.cool--;
      if (pit.cool <= 0 && Math.abs(pit.x - p.x) < 14) {
        pit.cool = 140;
        g.projectiles.push({ x: pit.x, y: pit.y - 0.5, vx: p.x < pit.x ? -0.09 : 0.09, owner: 'pitcher' });
      }
      if (p.swing > 0 && aabbOverlap(p.x + p.facing * 0.7 - 0.6, p.y - 1.2, 1.2, 1.4, pit.x - 0.4, pit.y - 0.8, 0.8, 0.8)) {
        pit.dead = true; sfx.stomp();
      }
    }
    for (var pj = g.projectiles.length - 1; pj >= 0; pj--) {
      var pr = g.projectiles[pj];
      pr.x += pr.vx;
      var prch = tileAt(g, Math.floor(pr.x), Math.floor(pr.y));
      if (isSolid(prch) || pr.x < -1 || pr.x > g.gw + 1) { g.projectiles.splice(pj, 1); continue; }
      if (pr.owner === 'pitcher') {
        if (p.swing > 0 && aabbOverlap(p.x + p.facing * 0.7 - 0.6, p.y - 1.2, 1.2, 1.4, pr.x - 0.2, pr.y - 0.2, 0.4, 0.4)) {
          var reflected = swingReflectsProjectile(pr.vx, p.facing);
          if (reflected !== 0) { pr.vx = reflected; pr.owner = 'player'; sfx.action(); continue; }
        }
        if (aabbOverlap(p.x - p.w / 2, p.y - ph, p.w, ph, pr.x - 0.2, pr.y - 0.2, 0.4, 0.4)) {
          hurtPlayer(p); g.projectiles.splice(pj, 1); continue;
        }
      } else {
        var hitSomething = false;
        for (var pk = 0; pk < g.pitchers.length; pk++) {
          var pt2 = g.pitchers[pk];
          if (!pt2.dead && aabbOverlap(pr.x - 0.2, pr.y - 0.2, 0.4, 0.4, pt2.x - 0.4, pt2.y - 0.8, 0.8, 0.8)) {
            pt2.dead = true; hitSomething = true; sfx.stomp(); break;
          }
        }
        for (var pk2 = 0; pk2 < g.enemies.length && !hitSomething; pk2++) {
          var en2 = g.enemies[pk2];
          if (!en2.dead && aabbOverlap(pr.x - 0.2, pr.y - 0.2, 0.4, 0.4, en2.x - 0.4, en2.y - 0.8, 0.8, 0.8)) {
            en2.dead = true; hitSomething = true; sfx.stomp(); break;
          }
        }
        if (hitSomething) { g.projectiles.splice(pj, 1); continue; }
      }
    }
  }

  function updateCamera(g, p){
    var viewW = W / TILE;
    var target = p.x + p.facing * 2 - viewW / 2;
    // Near the goal, bias the camera so the target is actually visible — otherwise a narrow
    // phone viewport has you shooting at a goal that's off the right edge.
    if (Math.abs(p.x - g.flag.x) < 10) {
      var goalTarget = g.flag.x - viewW * 0.62;
      target = target * 0.35 + goalTarget * 0.65;
    }
    var maxCam = g.gw - viewW;
    if (target < 0) target = 0;
    if (target > maxCam) target = Math.max(0, maxCam);
    g.camX += (target - g.camX) * 0.12;

    // Vertical follow — a camera WINDOW, not a grounded anchor.
    // Anchoring to the last grounded height meant the view never rose with a jump: with ~7 rows
    // visible and a 5.3-tile apex the player left the top of the screen entirely and vanished,
    // which also made it impossible to aim a jump over anything. Now the camera simply refuses to
    // let the player outside a band, so they are ALWAYS on screen while small hops still don't
    // shove the view around.
    var playH = Math.max(120, H - CONTROL_BAND);
    var viewH = playH / TILE;
    var yTarget;
    if (g.gh <= viewH) {
      yTarget = g.gh - viewH;                     // whole level fits: pin its floor to the bottom
    } else {
      if (typeof g.camY !== 'number') g.camY = p.y - viewH * 0.55;
      var rel = p.y - g.camY;                     // where the player sits inside the view
      var bandTop = viewH * 0.34, bandBot = viewH * 0.66;
      yTarget = g.camY;
      if (rel < bandTop) yTarget = p.y - bandTop;        // rising out of frame → follow up
      else if (rel > bandBot) yTarget = p.y - bandBot;   // falling out of frame → follow down
      if (yTarget < 0) yTarget = 0;
      if (yTarget > g.gh - viewH) yTarget = g.gh - viewH;
    }
    if (typeof g.camY !== 'number') g.camY = yTarget;
    // chase harder when the player is actually leaving the frame than when merely drifting
    var urgency = Math.abs(yTarget - g.camY) > viewH * 0.18 ? 0.3 : 0.14;
    g.camY += (yTarget - g.camY) * urgency;
  }

  // ---------- Draw helpers ----------
  function roundRect(x, y, w, h, r){
    ctx.beginPath();
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }
  function drawBall(world, x, y, r, roll){
    if (world === 0) { // football: white ball, dark pentagon
      ctx.fillStyle = '#F8FAFC';
      ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill();
      ctx.save(); ctx.translate(x, y); ctx.rotate(roll || 0);
      ctx.fillStyle = '#1F2937';
      ctx.beginPath();
      for (var k = 0; k < 5; k++) {
        var a = -Math.PI / 2 + k * (Math.PI * 2 / 5);
        ctx.lineTo(Math.cos(a) * r * 0.45, Math.sin(a) * r * 0.45);
      }
      ctx.closePath(); ctx.fill();
      ctx.restore();
    } else if (world === 1) { // basketball
      ctx.fillStyle = '#F97316';
      ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill();
      ctx.strokeStyle = '#7C2D12'; ctx.lineWidth = Math.max(1, r * 0.14);
      ctx.beginPath(); ctx.moveTo(x - r, y); ctx.lineTo(x + r, y);
      ctx.moveTo(x, y - r); ctx.lineTo(x, y + r);
      ctx.moveTo(x - r * 0.7, y - r * 0.7); ctx.quadraticCurveTo(x, y, x - r * 0.7, y + r * 0.7);
      ctx.moveTo(x + r * 0.7, y - r * 0.7); ctx.quadraticCurveTo(x, y, x + r * 0.7, y + r * 0.7);
      ctx.stroke();
    } else if (world === 2) { // fastball with stitches
      ctx.fillStyle = '#F8FAFC';
      ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill();
      ctx.strokeStyle = '#EF4444'; ctx.lineWidth = Math.max(1, r * 0.14);
      ctx.beginPath();
      ctx.moveTo(x - r * 0.6, y - r * 0.65); ctx.quadraticCurveTo(x - r * 0.05, y, x - r * 0.6, y + r * 0.65);
      ctx.moveTo(x + r * 0.6, y - r * 0.65); ctx.quadraticCurveTo(x + r * 0.05, y, x + r * 0.6, y + r * 0.65);
      ctx.stroke();
    } else { // gridiron ball
      ctx.fillStyle = '#92400E';
      ctx.beginPath(); ctx.ellipse(x, y, r * 1.2, r * 0.8, 0, 0, 7); ctx.fill();
      ctx.strokeStyle = '#F8FAFC'; ctx.lineWidth = Math.max(1, r * 0.12);
      ctx.beginPath(); ctx.moveTo(x - r * 0.5, y); ctx.lineTo(x + r * 0.5, y);
      for (var lz = -1; lz <= 1; lz++) { ctx.moveTo(x + lz * r * 0.3, y - r * 0.18); ctx.lineTo(x + lz * r * 0.3, y + r * 0.18); }
      ctx.stroke();
    }
  }
  function drawMonkey(px, py, size, facing, pose, t){
    // pose: 'idle' | 'run' | 'jump' | 'slide' | 'swing'
    ctx.save();
    ctx.translate(px, py);
    ctx.scale(facing, 1);
    var s = size;
    var run = pose === 'run' ? Math.sin(t * 0.35) : 0;
    var FUR = '#8B5A2B', FUR_D = '#6E4522', SKIN = '#D9B08C', DARK = '#231710';
    if (pose === 'slide') {
      // low horizontal glide
      ctx.rotate(-0.18);
      ctx.fillStyle = FUR;
      roundRect(-s * 0.5, -s * 0.42, s * 1.0, s * 0.4, s * 0.2); ctx.fill();
      ctx.beginPath(); ctx.arc(s * 0.42, -s * 0.42, s * 0.26, 0, 7); ctx.fill();
      ctx.fillStyle = SKIN;
      ctx.beginPath(); ctx.arc(s * 0.48, -s * 0.4, s * 0.16, 0, 7); ctx.fill();
      ctx.fillStyle = DARK;
      ctx.beginPath(); ctx.arc(s * 0.5, -s * 0.46, s * 0.035, 0, 7); ctx.fill();
      ctx.strokeStyle = FUR; ctx.lineWidth = s * 0.09;
      ctx.beginPath(); ctx.moveTo(-s * 0.45, -s * 0.25); ctx.quadraticCurveTo(-s * 0.8, -s * 0.5, -s * 0.7, -s * 0.75); ctx.stroke();
      ctx.restore();
      return;
    }
    // tail (behind)
    ctx.strokeStyle = FUR; ctx.lineWidth = s * 0.09; ctx.lineCap = 'round';
    var wag = Math.sin(t * 0.12) * 0.15;
    ctx.beginPath(); ctx.moveTo(-s * 0.28, -s * 0.2);
    ctx.quadraticCurveTo(-s * (0.62 + wag), -s * 0.35, -s * 0.52, -s * (0.7 + wag)); ctx.stroke();
    // legs
    ctx.strokeStyle = FUR_D; ctx.lineWidth = s * 0.13;
    if (pose === 'jump') {
      ctx.beginPath(); ctx.moveTo(-s * 0.12, -s * 0.12); ctx.lineTo(-s * 0.22, s * 0.02); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(s * 0.12, -s * 0.12); ctx.lineTo(s * 0.2, s * 0.0); ctx.stroke();
    } else {
      ctx.beginPath(); ctx.moveTo(-s * 0.12, -s * 0.15); ctx.lineTo(-s * 0.14 - run * s * 0.18, 0); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(s * 0.12, -s * 0.15); ctx.lineTo(s * 0.14 + run * s * 0.18, 0); ctx.stroke();
    }
    // body with jersey band in world accent
    ctx.fillStyle = FUR;
    roundRect(-s * 0.3, -s * 0.62, s * 0.6, s * 0.52, s * 0.18); ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    roundRect(-s * 0.3, -s * 0.5, s * 0.6, s * 0.16, s * 0.08); ctx.fill();
    // arms
    ctx.strokeStyle = FUR; ctx.lineWidth = s * 0.12;
    if (pose === 'jump' || pose === 'swing') {
      ctx.beginPath(); ctx.moveTo(-s * 0.26, -s * 0.5); ctx.lineTo(-s * 0.42, -s * 0.78); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(s * 0.26, -s * 0.5); ctx.lineTo(s * 0.44, -s * 0.82); ctx.stroke();
    } else {
      ctx.beginPath(); ctx.moveTo(-s * 0.26, -s * 0.48); ctx.lineTo(-s * 0.34 + run * s * 0.16, -s * 0.3); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(s * 0.26, -s * 0.48); ctx.lineTo(s * 0.34 - run * s * 0.16, -s * 0.3); ctx.stroke();
    }
    // head — big, friendly
    var hy = -s * 0.85;
    ctx.fillStyle = FUR;
    ctx.beginPath(); ctx.arc(0, hy, s * 0.34, 0, 7); ctx.fill();
    // ears with inner skin
    ctx.beginPath(); ctx.arc(-s * 0.34, hy - s * 0.05, s * 0.13, 0, 7); ctx.fill();
    ctx.beginPath(); ctx.arc(s * 0.34, hy - s * 0.05, s * 0.13, 0, 7); ctx.fill();
    ctx.fillStyle = SKIN;
    ctx.beginPath(); ctx.arc(-s * 0.34, hy - s * 0.05, s * 0.07, 0, 7); ctx.fill();
    ctx.beginPath(); ctx.arc(s * 0.34, hy - s * 0.05, s * 0.07, 0, 7); ctx.fill();
    // face patch
    ctx.beginPath();
    ctx.arc(-s * 0.1, hy - s * 0.04, s * 0.16, 0, 7);
    ctx.arc(s * 0.1, hy - s * 0.04, s * 0.16, 0, 7);
    ctx.arc(0, hy + s * 0.12, s * 0.2, 0, 7);
    ctx.fill();
    // eyes (blink every ~3s)
    var blink = (t % 190) < 6;
    ctx.fillStyle = DARK;
    if (blink) {
      ctx.fillRect(-s * 0.14, hy - s * 0.06, s * 0.1, s * 0.02);
      ctx.fillRect(s * 0.04, hy - s * 0.06, s * 0.1, s * 0.02);
    } else {
      ctx.beginPath(); ctx.arc(-s * 0.09, hy - s * 0.06, s * 0.05, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.arc(s * 0.09, hy - s * 0.06, s * 0.05, 0, 7); ctx.fill();
      ctx.fillStyle = '#FFF';
      ctx.beginPath(); ctx.arc(-s * 0.075, hy - s * 0.075, s * 0.016, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.arc(s * 0.105, hy - s * 0.075, s * 0.016, 0, 7); ctx.fill();
    }
    // nostrils + smile
    ctx.fillStyle = DARK;
    ctx.beginPath(); ctx.arc(-s * 0.03, hy + s * 0.08, s * 0.015, 0, 7); ctx.fill();
    ctx.beginPath(); ctx.arc(s * 0.03, hy + s * 0.08, s * 0.015, 0, 7); ctx.fill();
    ctx.strokeStyle = DARK; ctx.lineWidth = Math.max(1, s * 0.025);
    ctx.beginPath(); ctx.arc(0, hy + s * 0.1, s * 0.1, 0.35, Math.PI - 0.35); ctx.stroke();
    ctx.restore();
  }

  // ---------- Backdrops & ground textures (sport identity, AC-8b) ----------
  function drawBackdrop(world, camX){
    var sky = ctx.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, WORLDS[world].skyGradient[0]);
    sky.addColorStop(1, WORLDS[world].skyGradient[1]);
    ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
    // stars
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    for (var st = 0; st < 24; st++) {
      var sxp = ((st * 127 + 31) % 97) / 97;
      var syp = ((st * 61 + 17) % 53) / 53;
      ctx.fillRect(((sxp * W * 2 - camX * TILE * 0.08) % W + W) % W, syp * H * 0.4, 2, 2);
    }
    var horizon = H * 0.72;
    // floodlight pylons (slow parallax) — shared stadium language
    var lx0 = -((camX * TILE * 0.2) % 520);
    ctx.fillStyle = 'rgba(255,255,255,0.10)';
    for (var fl = lx0; fl < W + 80; fl += 520) {
      ctx.fillRect(fl, horizon - 150, 6, 150);
      for (var lb = 0; lb < 8; lb++) ctx.fillRect(fl - 18 + (lb % 4) * 12, horizon - 168 + Math.floor(lb / 4) * 10, 8, 6);
    }
    // stand band with crowd dots (medium parallax)
    ctx.fillStyle = 'rgba(255,255,255,0.055)';
    ctx.fillRect(0, horizon - 62, W, 62);
    var cx0 = -((camX * TILE * 0.35) % 14);
    for (var cr = cx0; cr < W; cr += 14) {
      for (var rr = 0; rr < 4; rr++) {
        ctx.fillStyle = 'rgba(255,255,255,' + (0.05 + ((cr + rr * 37) % 5) * 0.012) + ')';
        ctx.beginPath(); ctx.arc(cr + (rr % 2) * 6, horizon - 50 + rr * 13, 3, 0, 7); ctx.fill();
      }
    }
    // world silhouette props on the horizon (medium parallax)
    var px0 = -((camX * TILE * 0.35) % 420);
    ctx.strokeStyle = 'rgba(255,255,255,0.14)'; ctx.lineWidth = 3;
    for (var pp = px0; pp < W + 120; pp += 420) {
      ctx.beginPath();
      if (world === 0) { // goal
        ctx.moveTo(pp, horizon); ctx.lineTo(pp, horizon - 40); ctx.lineTo(pp + 60, horizon - 40); ctx.lineTo(pp + 60, horizon);
      } else if (world === 1) { // hoop
        ctx.moveTo(pp + 20, horizon); ctx.lineTo(pp + 20, horizon - 55);
        ctx.moveTo(pp + 6, horizon - 55); ctx.lineTo(pp + 34, horizon - 55); ctx.lineTo(pp + 34, horizon - 38); ctx.lineTo(pp + 6, horizon - 38); ctx.closePath();
      } else if (world === 2) { // foul pole
        ctx.moveTo(pp + 20, horizon); ctx.lineTo(pp + 20, horizon - 60);
        ctx.moveTo(pp + 20, horizon - 60); ctx.lineTo(pp + 34, horizon - 60);
      } else { // uprights
        ctx.moveTo(pp + 30, horizon); ctx.lineTo(pp + 30, horizon - 22);
        ctx.moveTo(pp + 8, horizon - 22); ctx.lineTo(pp + 52, horizon - 22);
        ctx.moveTo(pp + 8, horizon - 22); ctx.lineTo(pp + 8, horizon - 58);
        ctx.moveTo(pp + 52, horizon - 22); ctx.lineTo(pp + 52, horizon - 58);
      }
      ctx.stroke();
    }
  }
  function drawTile(world, ch, sx, sy, wx, topOpen){
    if (ch === '#') {
      ctx.fillStyle = WORLDS[world].tileTint;
      ctx.fillRect(sx, sy, TILE + 1, TILE + 1);
      if (topOpen) {
        // sport surface caps
        if (world === 0) { // grass + pitch line every 12 cols
          ctx.fillStyle = '#3E7A50';
          ctx.fillRect(sx, sy, TILE + 1, Math.max(4, TILE * 0.2));
          ctx.fillStyle = '#57A06B';
          for (var gb = 0; gb < 3; gb++) ctx.fillRect(sx + 4 + gb * (TILE / 3), sy - 3, 3, 5);
          if (wx % 12 === 0) { ctx.fillStyle = 'rgba(255,255,255,0.75)'; ctx.fillRect(sx + TILE / 2 - 2, sy, 4, TILE); }
        } else if (world === 1) { // parquet planks
          ctx.fillStyle = '#B07B3F';
          ctx.fillRect(sx, sy, TILE + 1, Math.max(4, TILE * 0.22));
          ctx.strokeStyle = 'rgba(0,0,0,0.35)'; ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(sx + (wx % 2 ? TILE * 0.5 : TILE * 0.25), sy);
          ctx.lineTo(sx + (wx % 2 ? TILE * 0.5 : TILE * 0.25), sy + TILE * 0.22);
          ctx.stroke();
        } else if (world === 2) { // infield dirt + base every 14
          ctx.fillStyle = '#B4713C';
          ctx.fillRect(sx, sy, TILE + 1, Math.max(4, TILE * 0.2));
          ctx.fillStyle = 'rgba(0,0,0,0.18)';
          ctx.fillRect(sx + 5, sy + 3, 3, 2); ctx.fillRect(sx + TILE - 10, sy + 5, 3, 2);
          if (wx % 14 === 0) {
            ctx.fillStyle = '#F8FAFC';
            ctx.save(); ctx.translate(sx + TILE / 2, sy + 2); ctx.rotate(Math.PI / 4);
            ctx.fillRect(-4, -4, 8, 8); ctx.restore();
          }
        } else { // turf + yard line every 6, numbers every 12
          ctx.fillStyle = '#2F6A46';
          ctx.fillRect(sx, sy, TILE + 1, Math.max(4, TILE * 0.2));
          if (wx % 6 === 0) { ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.fillRect(sx + TILE / 2 - 2, sy, 4, TILE); }
          if (wx % 12 === 0) {
            ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.font = 'bold ' + Math.floor(TILE * 0.32) + 'px system-ui'; ctx.textAlign = 'center';
            ctx.fillText(String((wx / 12) % 5 * 10 || 50), sx + TILE / 2 + 8, sy + TILE * 0.7);
            ctx.textAlign = 'left';
          }
        }
      } else {
        ctx.fillStyle = 'rgba(0,0,0,0.12)';
        if ((wx % 2) === 0) ctx.fillRect(sx, sy, TILE + 1, TILE + 1);
      }
    } else if (ch === 'B') {
      ctx.fillStyle = '#A9743B'; ctx.fillRect(sx + 1, sy + 1, TILE - 2, TILE - 2);
      ctx.strokeStyle = '#6B4423'; ctx.lineWidth = 2;
      ctx.strokeRect(sx + 2, sy + 2, TILE - 4, TILE - 4);
      ctx.beginPath(); ctx.moveTo(sx + 2, sy + 2); ctx.lineTo(sx + TILE - 2, sy + TILE - 2);
      ctx.moveTo(sx + TILE - 2, sy + 2); ctx.lineTo(sx + 2, sy + TILE - 2); ctx.stroke();
    } else if (ch === 'W') {
      ctx.fillStyle = '#4B5563'; ctx.fillRect(sx + 1, sy, TILE - 2, TILE + 1);
      ctx.fillStyle = '#6B7280';
      ctx.fillRect(sx + 4, sy + 4, 5, 5); ctx.fillRect(sx + TILE - 9, sy + 4, 5, 5);
      ctx.fillRect(sx + 4, sy + TILE - 9, 5, 5); ctx.fillRect(sx + TILE - 9, sy + TILE - 9, 5, 5);
    } else if (ch === '=') {
      ctx.fillStyle = WORLDS[world].accent;
      ctx.globalAlpha = 0.85;
      ctx.fillRect(sx, sy, TILE + 1, Math.max(5, TILE * 0.22));
      ctx.globalAlpha = 1;
    } else if (ch === '^') {
      ctx.fillStyle = '#94A3B8';
      ctx.beginPath();
      ctx.moveTo(sx, sy + TILE); ctx.lineTo(sx + TILE / 2, sy + TILE * 0.25); ctx.lineTo(sx + TILE, sy + TILE);
      ctx.closePath(); ctx.fill();
    } else if (ch === 'h') {
      // Athletics hurdle — angled legs + a hazard-striped top bar, with the duck-under gap left
      // visibly open. (Straight posts + a flat bar read as a tennis net / table.)
      ctx.strokeStyle = '#D1D5DB'; ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(sx + TILE * 0.16, sy + TILE * 0.3); ctx.lineTo(sx + TILE * 0.02, sy + TILE);
      ctx.moveTo(sx + TILE * 0.16, sy + TILE * 0.3); ctx.lineTo(sx + TILE * 0.34, sy + TILE);
      ctx.moveTo(sx + TILE * 0.84, sy + TILE * 0.3); ctx.lineTo(sx + TILE * 0.7, sy + TILE);
      ctx.moveTo(sx + TILE * 0.84, sy + TILE * 0.3); ctx.lineTo(sx + TILE * 0.98, sy + TILE);
      ctx.stroke();
      var barH = Math.max(6, TILE * 0.2), barY = sy + TILE * 0.16;
      ctx.fillStyle = WORLDS[world].accent;
      ctx.fillRect(sx, barY, TILE + 1, barH);
      ctx.save();
      ctx.beginPath(); ctx.rect(sx, barY, TILE + 1, barH); ctx.clip();
      ctx.strokeStyle = 'rgba(15,10,26,0.55)'; ctx.lineWidth = 4;
      for (var hz = -2; hz < 5; hz++) {
        ctx.beginPath();
        ctx.moveTo(sx + hz * (TILE / 3), barY + barH);
        ctx.lineTo(sx + hz * (TILE / 3) + barH, barY);
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  /**
   * The goal is defended — the keeper patrols the mouth on a deterministic path, so its position
   * can be read and beaten rather than guessed.
   */
  /**
   * In-level finish marker. Deliberately just a gate: the sport goal itself lives in the composed
   * shootout scene, because drawing goal structures at world coordinates inside scrolling terrain
   * meant they clipped, overlapped tiles and fell off narrow screens.
   */
  function drawFinishGate(g, fx, fy, ticks){
    var acc = WORLDS[g.world].accent;
    var w = TILE * 1.5, h = TILE * 2.3;
    var pulse = 0.5 + 0.5 * Math.sin(ticks * 0.07);
    ctx.globalAlpha = 0.16 + 0.16 * pulse;
    ctx.fillStyle = acc;
    ctx.fillRect(fx - w / 2, fy - h, w, h);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = acc; ctx.lineWidth = 5; ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(fx - w / 2, fy); ctx.lineTo(fx - w / 2, fy - h);
    ctx.lineTo(fx + w / 2, fy - h); ctx.lineTo(fx + w / 2, fy);
    ctx.stroke();
    // chevrons pointing in
    ctx.strokeStyle = 'rgba(255,255,255,0.75)'; ctx.lineWidth = 3;
    for (var i = 0; i < 3; i++) {
      var cy = fy - h * 0.3 - i * TILE * 0.4 + (pulse * TILE * 0.15);
      ctx.beginPath();
      ctx.moveTo(fx - TILE * 0.28, cy - TILE * 0.18);
      ctx.lineTo(fx, cy);
      ctx.lineTo(fx + TILE * 0.28, cy - TILE * 0.18);
      ctx.stroke();
    }
    ctx.fillStyle = acc;
    ctx.font = 'bold ' + Math.floor(TILE * 0.3) + 'px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('FINISH', fx, fy - h - TILE * 0.25);
    ctx.textAlign = 'left';
  }

  // ---------- HUD ----------
  function drawHud(g){
    var p = g.player;
    var hudY = insetTop + 14;
    for (var i = 0; i < PHYS.HEARTS; i++) {
      ctx.fillStyle = i < p.hearts ? '#F87171' : 'rgba(255,255,255,0.15)';
      var hx = HUD_LEFT + 6 + i * 26, hy = hudY + HUD_ROW2;
      ctx.beginPath();
      ctx.arc(hx - 4, hy, 5.5, 0, 7); ctx.arc(hx + 4, hy, 5.5, 0, 7);
      ctx.moveTo(hx - 9, hy + 2); ctx.lineTo(hx, hy + 13); ctx.lineTo(hx + 9, hy + 2);
      ctx.closePath(); ctx.fill();
    }
    ctx.fillStyle = '#FCD34D';
    ctx.beginPath(); ctx.arc(HUD_LEFT + 6, hudY + HUD_ROW2 + 32, 8, 0, 7); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.font = 'bold 15px system-ui'; ctx.textAlign = 'left';
    ctx.fillText(g.coinsGot + '/' + g.coinTotal, HUD_LEFT + 22, hudY + HUD_ROW2 + 37);
    if (p.jetFuel > 0) {
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      roundRect(HUD_LEFT, hudY + HUD_ROW2 + 48, 80, 8, 4); ctx.fill();
      ctx.fillStyle = '#FB923C';
      roundRect(HUD_LEFT, hudY + HUD_ROW2 + 48, 80 * (p.jetFuel / PHYS.JET_FUEL_TICKS), 8, 4); ctx.fill();
    }
    ctx.textAlign = 'center'; ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.font = 'bold 14px system-ui';
    ctx.fillText(WORLDS[g.world].name.toUpperCase() + '  ' + g.slot + '/' + LEVELS_PER_WORLD, W / 2, hudY + 12);
    soundHit.x = W - 30; soundHit.y = hudY + 8;
    ctx.font = '16px system-ui';
    ctx.fillText(audioOn ? '\\uD83D\\uDD0A' : '\\uD83D\\uDD07', W - 30, hudY + 14);
    // No on-screen controls (AC-23): the left half steers by drag and the right half takes
    // gestures, so nothing is painted over the playfield. A fading first-run hint teaches them.
    if (ROT && g.ticks < 420) {
      // the stage is already landscape; the player just needs to turn the handset to match
      ctx.globalAlpha = 0.85;
      ctx.fillStyle = '#A78BFA'; ctx.font = 'bold 13px system-ui'; ctx.textAlign = 'center';
      ctx.fillText('\u21BB  TURN YOUR PHONE SIDEWAYS', W / 2, 22);
      ctx.textAlign = 'left'; ctx.globalAlpha = 1;
    }
    if (g.ticks < 320) {
      var fade = g.ticks > 250 ? (320 - g.ticks) / 70 : 1;
      ctx.globalAlpha = 0.75 * fade;
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('HOLD LEFT & SLIDE TO RUN', W * 0.25, H - 16);
      ctx.fillText('TAP = JUMP  \u00B7  TAP TOP = ' + WORLDS[g.world].action.label + '  \u00B7  SWIPE \u2193 = SLIDE', W * 0.75, H - 16);
      ctx.textAlign = 'left';
      ctx.globalAlpha = 1;
    }

    ctx.textAlign = 'left';
  }

  // ---------- Play scene ----------
  function drawPlay(){
    var g = game;
    drawBackdrop(g.world, g.camX);
    var offY = -(g.camY || 0) * TILE;   // world y → screen y = (wy - camY) * TILE
    var startX = Math.floor(g.camX), endX = Math.ceil(g.camX + W / TILE) + 1;
    for (var y = 0; y < g.gh; y++) {
      for (var x = startX; x < Math.min(endX, g.gw); x++) {
        var ch = g.grid[y][x];
        if (ch !== ' ') {
          var topOpen = y === 0 || g.grid[y - 1][x] === ' ';
          drawTile(g.world, ch, (x - g.camX) * TILE, offY + y * TILE, x, topOpen);
        }
      }
    }
    // the sport goal — this IS the finish; no separate flag (it read as clutter next to the goal)
    var fx = (g.flag.x - g.camX) * TILE, fy = offY + g.flag.y * TILE;
    drawFinishGate(g, fx, fy, g.ticks);
    // checkpoints
    for (var ki = 0; ki < g.checks.length; ki++) {
      var kc = g.checks[ki];
      var kx = (kc.x - g.camX) * TILE, ky = offY + kc.y * TILE;
      ctx.strokeStyle = kc.lit ? WORLDS[g.world].accent : 'rgba(255,255,255,0.4)'; ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(kx, ky); ctx.lineTo(kx, ky - TILE * 1.2); ctx.stroke();
      ctx.fillStyle = kc.lit ? WORLDS[g.world].accent : 'rgba(255,255,255,0.25)';
      ctx.beginPath(); ctx.moveTo(kx, ky - TILE * 1.2); ctx.lineTo(kx + TILE * 0.45, ky - TILE * 1.05); ctx.lineTo(kx, ky - TILE * 0.9);
      ctx.closePath(); ctx.fill();
    }
    // ropes (anchor + line; the grabbed rope draws to the player)
    for (var ri = 0; ri < g.ropes.length; ri++) {
      var ra = g.ropes[ri];
      var rx = (ra.x - g.camX) * TILE, ry = offY + ra.y * TILE;
      ctx.fillStyle = '#C89F7B';
      ctx.beginPath(); ctx.arc(rx, ry, TILE * 0.12, 0, 7); ctx.fill();
      var grabbed = g.player.rope && g.player.rope.ax === ra.x && g.player.rope.ay === ra.y;
      ctx.strokeStyle = '#C89F7B'; ctx.lineWidth = Math.max(2, TILE * 0.08);
      ctx.beginPath(); ctx.moveTo(rx, ry);
      if (grabbed) {
        ctx.lineTo((g.player.x - g.camX) * TILE, offY + (g.player.y - 0.5) * TILE);
      } else {
        var sway = Math.sin(g.ticks * 0.03 + ri) * TILE * 0.3;
        ctx.quadraticCurveTo(rx + sway * 0.4, ry + PHYS.ROPE_LEN * TILE * 0.55, rx + sway, ry + PHYS.ROPE_LEN * TILE);
      }
      ctx.stroke();
    }
    // springs
    for (var si = 0; si < g.springs.length; si++) {
      var sp = g.springs[si];
      var spx = (sp.x - g.camX) * TILE, spy = offY + sp.y * TILE;
      var squish = sp.squash > 0 ? 0.5 : 1;
      ctx.strokeStyle = '#9CA3AF'; ctx.lineWidth = 3;
      ctx.beginPath();
      for (var coil = 0; coil < 3; coil++) {
        var cy = spy - 4 - coil * 7 * squish;
        ctx.moveTo(spx - TILE * 0.28, cy); ctx.lineTo(spx + TILE * 0.28, cy - 3);
      }
      ctx.stroke();
      ctx.fillStyle = WORLDS[g.world].accent;
      roundRect(spx - TILE * 0.38, spy - 8 - 21 * squish, TILE * 0.76, 6, 3); ctx.fill();
    }
    // jetpack pickups
    for (var ji = 0; ji < g.jets.length; ji++) {
      var jp = g.jets[ji];
      if (jp.got) continue;
      var jx = (jp.x - g.camX) * TILE, jy = offY + jp.y * TILE + Math.sin(g.ticks * 0.08 + ji) * 4;
      ctx.fillStyle = '#6B7280';
      roundRect(jx - TILE * 0.22, jy - TILE * 0.3, TILE * 0.44, TILE * 0.55, 6); ctx.fill();
      ctx.fillStyle = '#FB923C';
      ctx.beginPath(); ctx.moveTo(jx - TILE * 0.12, jy + TILE * 0.26); ctx.lineTo(jx, jy + TILE * 0.45); ctx.lineTo(jx + TILE * 0.12, jy + TILE * 0.26);
      ctx.closePath(); ctx.fill();
    }
    // coins
    for (var ci = 0; ci < g.coins.length; ci++) {
      var c = g.coins[ci];
      if (c.got) continue;
      var pulse = 0.85 + 0.15 * Math.sin(g.ticks * 0.12 + ci);
      ctx.fillStyle = '#FCD34D';
      ctx.beginPath(); ctx.arc((c.x - g.camX) * TILE, offY + c.y * TILE, TILE * 0.22 * pulse, 0, 7); ctx.fill();
      ctx.fillStyle = '#B45309'; ctx.font = 'bold ' + Math.floor(TILE * 0.24) + 'px system-ui'; ctx.textAlign = 'center';
      ctx.fillText('z', (c.x - g.camX) * TILE, offY + c.y * TILE + TILE * 0.08);
      ctx.textAlign = 'left';
    }
    // moving platforms
    for (var lp = 0; lp < g.platforms.length; lp++) {
      var plat = g.platforms[lp];
      var plx = (plat.x - g.camX) * TILE, ply = offY + plat.y * TILE;
      ctx.fillStyle = WORLDS[g.world].accent;
      roundRect(plx, ply, plat.w * TILE, Math.max(6, TILE * 0.28), 5); ctx.fill();
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      roundRect(plx + 4, ply + Math.max(6, TILE * 0.28) - 4, plat.w * TILE - 8, 4, 2); ctx.fill();
    }
    // enemies — sport ball skins
    for (var ei = 0; ei < g.enemies.length; ei++) {
      var e = g.enemies[ei];
      if (e.dead) continue;
      var ex = (e.x - g.camX) * TILE, ey = offY + e.y * TILE;
      if (g.world === 3) {
        // blocking-sled dummy
        ctx.fillStyle = e.stun > 0 ? '#9CA3AF' : '#B91C1C';
        roundRect(ex - TILE * 0.34, ey - TILE * 0.78, TILE * 0.68, TILE * 0.78, TILE * 0.16); ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        roundRect(ex - TILE * 0.34, ey - TILE * 0.5, TILE * 0.68, TILE * 0.12, 4); ctx.fill();
      } else {
        if (e.stun > 0) { ctx.globalAlpha = 0.55; }
        drawBall(g.world, ex, ey - TILE * 0.36, TILE * 0.36, e.x * 2);
        ctx.globalAlpha = 1;
        if (e.charge > 0) { // charging tell
          ctx.strokeStyle = '#F87171'; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.arc(ex, ey - TILE * 0.36, TILE * 0.46, 0, 7); ctx.stroke();
        }
      }
      // eyes on everything — it's a cartoon
      ctx.fillStyle = '#fff';
      ctx.beginPath(); ctx.arc(ex - TILE * 0.12, ey - TILE * 0.46, TILE * 0.07, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.arc(ex + TILE * 0.12, ey - TILE * 0.46, TILE * 0.07, 0, 7); ctx.fill();
      ctx.fillStyle = '#1F2937';
      var look = e.stun > 0 ? 0 : e.dir * TILE * 0.03;
      ctx.beginPath(); ctx.arc(ex - TILE * 0.12 + look, ey - TILE * 0.46, TILE * 0.035, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.arc(ex + TILE * 0.12 + look, ey - TILE * 0.46, TILE * 0.035, 0, 7); ctx.fill();
    }
    // flyers
    for (var fi = 0; fi < g.flyers.length; fi++) {
      var fl2 = g.flyers[fi];
      if (fl2.dead) continue;
      var fx2 = (fl2.x - g.camX) * TILE, fy2 = offY + fl2.y * TILE;
      drawBall(g.world, fx2, fy2, TILE * 0.3, g.ticks * 0.05);
      // little wings
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      var flap = Math.sin(g.ticks * 0.3 + fl2.phase) * TILE * 0.12;
      ctx.beginPath(); ctx.ellipse(fx2 - TILE * 0.34, fy2 - TILE * 0.1 - flap, TILE * 0.16, TILE * 0.07, -0.4, 0, 7); ctx.fill();
      ctx.beginPath(); ctx.ellipse(fx2 + TILE * 0.34, fy2 - TILE * 0.1 - flap, TILE * 0.16, TILE * 0.07, 0.4, 0, 7); ctx.fill();
    }
    // pitchers
    for (var pi = 0; pi < g.pitchers.length; pi++) {
      var pit = g.pitchers[pi];
      if (pit.dead) continue;
      var px2 = (pit.x - g.camX) * TILE, py2 = offY + pit.y * TILE;
      ctx.fillStyle = '#475569';
      roundRect(px2 - TILE * 0.35, py2 - TILE * 0.7, TILE * 0.7, TILE * 0.7, 6); ctx.fill();
      ctx.fillStyle = '#0F172A';
      ctx.beginPath(); ctx.arc(px2 + (pit.cool < 30 ? Math.sin(g.ticks) * 2 : 0), py2 - TILE * 0.42, TILE * 0.14, 0, 7); ctx.fill();
      drawBall(2, px2, py2 - TILE * 0.42, TILE * 0.1, 0);
    }
    // projectiles + kicked ball
    for (var pj = 0; pj < g.projectiles.length; pj++) {
      var pr = g.projectiles[pj];
      if (pr.owner === 'player') {
        ctx.fillStyle = '#FCD34D';
        ctx.beginPath(); ctx.arc((pr.x - g.camX) * TILE, offY + pr.y * TILE, TILE * 0.17, 0, 7); ctx.fill();
      } else {
        drawBall(2, (pr.x - g.camX) * TILE, offY + pr.y * TILE, TILE * 0.15, pr.x * 3);
      }
    }
    if (g.ball) drawBall(g.world, (g.ball.x - g.camX) * TILE, offY + g.ball.y * TILE, TILE * 0.22, g.ball.spin || 0);
    // particles
    for (var pa = 0; pa < g.particles.length; pa++) {
      var part = g.particles[pa];
      ctx.globalAlpha = part.life / 24;
      ctx.fillStyle = part.color || '#A9743B';
      ctx.fillRect((part.x - g.camX) * TILE - 2, offY + part.y * TILE - 2, 4, 4);
    }
    ctx.globalAlpha = 1;
    // player
    var p = g.player;
    if (p.invuln === 0 || Math.floor(g.ticks / 4) % 2 === 0) {
      var pose = 'idle';
      if (p.rope) pose = 'swing';
      else if (p.sliding > 0) pose = 'slide';
      else if (!p.grounded) pose = 'jump';
      else if (Math.abs(p.vx) > 0.02) pose = 'run';
      drawMonkey((p.x - g.camX) * TILE, offY + p.y * TILE, TILE * 1.02, p.facing, pose, g.ticks);
      if (p.jetFuel > 0) {
        ctx.fillStyle = '#6B7280';
        roundRect((p.x - p.facing * 0.32 - g.camX) * TILE - TILE * 0.12, offY + (p.y - 0.75) * TILE, TILE * 0.24, TILE * 0.4, 4); ctx.fill();
      }
      if (p.swing > 0) {
        ctx.strokeStyle = WORLDS[g.world].accent; ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc((p.x - g.camX) * TILE, offY + (p.y - 0.6) * TILE, TILE * 0.9, p.facing > 0 ? -1.2 : Math.PI - 0.6, p.facing > 0 ? 0.6 : Math.PI + 1.2);
        ctx.stroke();
      }
    }
    drawHud(g);
  }


  // ---------- Shootout: the level's finale, composed in SCREEN space ----------
  /**
   * Two timed decisions — place the ball, then strike it. Laid out relative to the canvas, so the
   * goal, keeper, mascot and meters always compose correctly at any viewport size (the old
   * world-space goal clipped and overlapped terrain). Attempts are not capped; the shot count
   * feeds the star rating, so accuracy is rewarded rather than punished with a dead end.
   */
  var SO = null;
  function startShootout(g){
    SO = {
      phase: 'aim', tick: 0, aim: 0, power: 0, shots: 0,
      ballT: 0, ballAim: 0, outcome: '', resultTicks: 0, keeperShown: 0,
    };
    scene = 'shootout';
    sfx.checkpoint();
  }
  function shootoutLabel(kind){
    if (kind === 'kick') return 'PENALTY';
    if (kind === 'shoot') return 'FREE THROW';
    if (kind === 'bat') return 'AT BAT';
    return 'FIELD GOAL';
  }
  function shootoutTap(){
    if (!SO) return;
    if (SO.phase === 'aim') { SO.phase = 'power'; SO.tick = 0; sfx.action(); return; }
    if (SO.phase === 'power') {
      SO.ballAim = SO.aim;
      SO.keeperShown = shootoutKeeperAt(SO.tick, game.slot, MONKEY_WORLDS_SHOOTOUT);
      SO.outcome = shootoutOutcome(SO.aim, SO.keeperShown, SO.power, MONKEY_WORLDS_SHOOTOUT);
      SO.shots++;
      SO.phase = 'fly'; SO.ballT = 0;
      sfx.action();
      return;
    }
    if (SO.phase === 'result' && SO.outcome !== 'goal') {
      SO.phase = 'aim'; SO.tick = 0; SO.outcome = '';
    }
  }
  function updateShootout(){
    if (!SO || !game || scene !== 'shootout') return;
    SO.tick++;
    var S = MONKEY_WORLDS_SHOOTOUT;
    if (SO.phase === 'aim') {
      // sweep the placement marker across the mouth
      SO.aim = (shotPower(SO.tick, Math.max(30, S.AIM_PERIOD - (game.slot - 1) * 8)) * 2) - 1;
    } else if (SO.phase === 'power') {
      SO.power = shotPower(SO.tick, S.POWER_PERIOD);
      SO.keeperShown = shootoutKeeperAt(SO.tick, game.slot, MONKEY_WORLDS_SHOOTOUT);
    } else if (SO.phase === 'fly') {
      SO.ballT += 0.045;
      if (SO.ballT >= 1) {
        SO.phase = 'result'; SO.resultTicks = 0;
        if (SO.outcome === 'goal') { sfx.clear(); } else { sfx.hurt(); }
      }
    } else if (SO.phase === 'result') {
      SO.resultTicks++;
      if (SO.outcome === 'goal' && SO.resultTicks > 40) {
        game.shots = SO.shots;
        endLevel(true);
      }
    }
    if (SO.phase !== 'power' && SO.phase !== 'fly') {
      SO.keeperShown = shootoutKeeperAt(SO.tick, game.slot, MONKEY_WORLDS_SHOOTOUT);
    }
  }
  function drawShootout(){
    var g = game, S = MONKEY_WORLDS_SHOOTOUT;
    var kind = WORLDS[g.world].action.type, acc = WORLDS[g.world].accent;
    drawBackdrop(g.world, 0);
    // Composed as a behind-the-taker view: HUD up top, the goal framed in the middle band, the
    // mascot in the foreground below it. Everything is derived from W/H so it fits any viewport.
    // Composition comes from the shared pure helper so the drawn scene and the layout the tests
    // exercise can never drift (see shootoutLayout / T-38).
    var LAY = shootoutLayout(W, H, insetTop);
    var goalCX = W / 2;
    var goalW = LAY.goalW, goalH = LAY.goalH;
    var goalTop = LAY.goalTop;
    var groundY = LAY.groundY;
    var shooterY = LAY.shooterY;
    // turf between the goal line and the foreground
    var turf = ctx.createLinearGradient(0, groundY, 0, shooterY + 20);
    turf.addColorStop(0, 'rgba(255,255,255,0.11)');
    turf.addColorStop(1, 'rgba(255,255,255,0.02)');
    ctx.fillStyle = turf;
    ctx.fillRect(0, groundY, W, shooterY + 20 - groundY);
    // Perspective lines from the spot out to the goal — they fill the space between the two focal
    // points and sell the depth, instead of leaving a dead band.
    ctx.strokeStyle = 'rgba(255,255,255,0.10)'; ctx.lineWidth = 1.5;
    for (var pl = -3; pl <= 3; pl++) {
      ctx.beginPath();
      ctx.moveTo(goalCX + pl * (goalW / 6), groundY);
      ctx.lineTo(goalCX + pl * (W * 0.42), shooterY + 18);
      ctx.stroke();
    }
    var band = (shooterY + 18 - groundY);
    for (var pr = 1; pr <= 3; pr++) {
      var py = groundY + band * (pr / 3.4) * (pr / 3.4);
      ctx.globalAlpha = 0.5;
      ctx.beginPath(); ctx.moveTo(0, py); ctx.lineTo(W, py); ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.strokeStyle = 'rgba(255,255,255,0.24)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(0, groundY); ctx.lineTo(W, groundY); ctx.stroke();
    // spot the ball is struck from
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    ctx.beginPath(); ctx.ellipse(goalCX, shooterY + 6, 34, 8, 0, 0, 7); ctx.fill();
    // the goal, per sport, drawn to the SAME box so nothing can drift
    ctx.lineCap = 'round';
    if (kind === 'kick') {
      ctx.strokeStyle = 'rgba(255,255,255,0.28)'; ctx.lineWidth = 1;
      for (var n = 1; n < 8; n++) {
        var nx = goalCX - goalW / 2 + (n * goalW) / 8;
        ctx.beginPath(); ctx.moveTo(nx, goalTop); ctx.lineTo(nx, groundY); ctx.stroke();
      }
      for (var m = 1; m < 4; m++) {
        var ny = goalTop + (m * goalH) / 4;
        ctx.beginPath(); ctx.moveTo(goalCX - goalW / 2, ny); ctx.lineTo(goalCX + goalW / 2, ny); ctx.stroke();
      }
      ctx.strokeStyle = '#F8FAFC'; ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.moveTo(goalCX - goalW / 2, groundY); ctx.lineTo(goalCX - goalW / 2, goalTop);
      ctx.lineTo(goalCX + goalW / 2, goalTop); ctx.lineTo(goalCX + goalW / 2, groundY);
      ctx.stroke();
    } else if (kind === 'shoot') {
      ctx.fillStyle = '#F8FAFC';
      ctx.fillRect(goalCX - goalW * 0.34, goalTop - goalH * 0.1, goalW * 0.68, goalH * 0.62);
      ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 3;
      ctx.strokeRect(goalCX - goalW * 0.16, goalTop + goalH * 0.16, goalW * 0.32, goalH * 0.3);
      ctx.strokeStyle = '#F97316'; ctx.lineWidth = 7;
      ctx.beginPath(); ctx.ellipse(goalCX, goalTop + goalH * 0.56, goalW * 0.22, goalH * 0.07, 0, 0, 7); ctx.stroke();
      ctx.strokeStyle = 'rgba(255,255,255,0.8)'; ctx.lineWidth = 2;
      for (var q = 0; q <= 6; q++) {
        var qa = Math.PI + (q / 6) * Math.PI;
        ctx.beginPath();
        ctx.moveTo(goalCX + Math.cos(qa) * goalW * 0.22, goalTop + goalH * 0.56);
        ctx.lineTo(goalCX + Math.cos(qa) * goalW * 0.11, goalTop + goalH * 0.86);
        ctx.stroke();
      }
    } else if (kind === 'bat') {
      ctx.fillStyle = '#1F3A2E';
      ctx.fillRect(goalCX - goalW / 2, goalTop + goalH * 0.55, goalW, goalH * 0.45);
      ctx.fillStyle = '#FCD34D';
      ctx.fillRect(goalCX - goalW / 2, goalTop + goalH * 0.55 - 6, goalW, 6);
      ctx.fillStyle = acc; ctx.globalAlpha = 0.16;
      ctx.fillRect(goalCX - goalW / 2, goalTop, goalW, goalH * 0.55);
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#FCD34D'; ctx.font = 'bold 13px system-ui'; ctx.textAlign = 'center';
      ctx.fillText('CLEAR THE WALL', Math.min(W - 76, Math.max(76, goalCX)), goalTop - 12);
      ctx.textAlign = 'left';
    } else {
      ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.moveTo(goalCX, groundY); ctx.lineTo(goalCX, goalTop + goalH * 0.55);
      ctx.moveTo(goalCX - goalW / 2, goalTop + goalH * 0.55); ctx.lineTo(goalCX + goalW / 2, goalTop + goalH * 0.55);
      ctx.moveTo(goalCX - goalW / 2, goalTop + goalH * 0.55); ctx.lineTo(goalCX - goalW / 2, goalTop - goalH * 0.35);
      ctx.moveTo(goalCX + goalW / 2, goalTop + goalH * 0.55); ctx.lineTo(goalCX + goalW / 2, goalTop - goalH * 0.35);
      ctx.stroke();
    }
    // Keeper. Horizontal position is the rule (aim vs keeperX); the line it stands on is per sport
    // and purely presentational. Drawn as a figure in a light kit — a slab in the world's own
    // accent blended into the backdrop and swallowed the reticle.
    var mouthHalf = goalW / 2;
    var kx = goalCX + SO.keeperShown * mouthHalf;
    var kw = S.KEEPER_HALF * mouthHalf;          // honest: this IS the reach in the rules
    var kh = goalH * 0.46;
    var kFeet = kind === 'bat' ? goalTop + goalH * 0.55        // on the wall top
      : kind === 'shoot' ? goalTop + goalH * 0.9
      : groundY;                                                // on the goal line
    var beaten = SO.phase === 'result' && SO.outcome === 'goal';
    var kit = beaten ? 'rgba(248,250,252,0.35)'
      : SO.outcome === 'saved' && SO.phase === 'result' ? '#FCD34D' : '#E8EDF5';
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 10;
    // arms out to the full reach — the box you must beat is legible
    ctx.strokeStyle = kit; ctx.lineWidth = Math.max(5, kh * 0.13); ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(kx - kw, kFeet - kh * 0.72);
    ctx.lineTo(kx, kFeet - kh * 0.62);
    ctx.lineTo(kx + kw, kFeet - kh * 0.72);
    ctx.stroke();
    // torso + legs
    ctx.fillStyle = kit;
    roundRect(kx - kh * 0.17, kFeet - kh * 0.72, kh * 0.34, kh * 0.48, kh * 0.1); ctx.fill();
    ctx.lineWidth = Math.max(4, kh * 0.11);
    ctx.beginPath();
    ctx.moveTo(kx - kh * 0.09, kFeet - kh * 0.24); ctx.lineTo(kx - kh * 0.13, kFeet);
    ctx.moveTo(kx + kh * 0.09, kFeet - kh * 0.24); ctx.lineTo(kx + kh * 0.13, kFeet);
    ctx.stroke();
    // head
    ctx.beginPath(); ctx.arc(kx, kFeet - kh * 0.86, kh * 0.15, 0, 7); ctx.fill();
    ctx.restore();
    // gloves mark the exact edges of the reach
    ctx.fillStyle = kind === 'kick' ? '#F97316' : kit;
    ctx.beginPath(); ctx.arc(kx - kw, kFeet - kh * 0.72, Math.max(5, kh * 0.1), 0, 7); ctx.fill();
    ctx.beginPath(); ctx.arc(kx + kw, kFeet - kh * 0.72, Math.max(5, kh * 0.1), 0, 7); ctx.fill();
    // the mascot, taking the shot
    var shooterX = goalCX;
    drawMonkey(shooterX, shooterY, Math.min(TILE * 1.2, goalH * 0.42), 1, SO.phase === 'fly' ? 'jump' : 'idle', SO.tick);
    // Aim marker — drawn last so it is never hidden behind the keeper, with a dark halo so it
    // reads against any world's backdrop.
    if (SO.phase === 'aim' || SO.phase === 'power') {
      var ax = goalCX + SO.aim * mouthHalf;
      var ay = goalTop + goalH * 0.3;
      var locked = SO.phase === 'power';
      ctx.save();
      ctx.lineCap = 'round';
      ctx.strokeStyle = 'rgba(15,10,26,0.8)'; ctx.lineWidth = 7;
      ctx.beginPath(); ctx.arc(ax, ay, 14, 0, 7); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(ax - 21, ay); ctx.lineTo(ax + 21, ay);
      ctx.moveTo(ax, ay - 21); ctx.lineTo(ax, ay + 21);
      ctx.stroke();
      ctx.strokeStyle = locked ? '#FCD34D' : '#FFFFFF'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(ax, ay, 14, 0, 7); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(ax - 21, ay); ctx.lineTo(ax + 21, ay);
      ctx.moveTo(ax, ay - 21); ctx.lineTo(ax, ay + 21);
      ctx.stroke();
      ctx.fillStyle = locked ? '#FCD34D' : '#FFFFFF';
      ctx.beginPath(); ctx.arc(ax, ay, 4, 0, 7); ctx.fill();
      ctx.restore();
    }
    // ball in flight
    if (SO.phase === 'fly' || SO.phase === 'result') {
      var t = Math.min(1, SO.ballT);
      var tx = goalCX + SO.ballAim * mouthHalf;
      var ty = goalTop + goalH * 0.3;
      var bx = shooterX + (tx - shooterX) * t;
      var by = shooterY - TILE * 0.4 + (ty - (shooterY - TILE * 0.4)) * t - Math.sin(t * Math.PI) * 26;
      if (SO.outcome === 'short') by = Math.max(by, shooterY - TILE * 0.4 - (1 - t) * 10);
      if (SO.outcome === 'over') by -= goalH * 0.75 * t * t;
      drawBall(g.world, bx, by, 12, SO.ballT * 8);
    }
    // ---- HUD (screen space, always fits) ----
    var hudY = insetTop + 14;
    ctx.textAlign = 'center';
    ctx.fillStyle = '#fff'; ctx.font = 'bold 20px system-ui';
    ctx.fillText(shootoutLabel(kind), W / 2, hudY + 16);
    ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.font = '13px system-ui';
    ctx.fillText('Attempt ' + (SO.shots + (SO.phase === 'result' ? 0 : 1)), W / 2, hudY + 38);
    // D-6: register the speaker here too. handleTap tests soundHit first, and this scene used to
    // leave it holding the play HUD's coordinates — so a tap near the top-right silently toggled
    // audio instead of taking the shot.
    soundHit.x = W - 30; soundHit.y = hudY + 8;
    ctx.font = '16px system-ui';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillText(audioOn ? '\uD83D\uDD0A' : '\uD83D\uDD07', W - 30, hudY + 14);
    var prompt = SO.phase === 'aim' ? 'TAP TO PLACE YOUR SHOT'
      : SO.phase === 'power' ? 'TAP TO STRIKE'
      : SO.phase === 'fly' ? '' : (SO.outcome === 'goal' ? '' : 'TAP TO GO AGAIN');
    if (prompt) {
      ctx.fillStyle = acc; ctx.font = 'bold 15px system-ui';
      ctx.globalAlpha = 0.75 + 0.25 * Math.sin(SO.tick * 0.13);
      ctx.fillText(prompt, W / 2, LAY.promptY);
      ctx.globalAlpha = 1;
    }
    // power meter — only while charging, so the two decisions never compete for attention
    if (SO.phase === 'power') {
      var mw = Math.min(240, W * 0.6), mx = W / 2 - mw / 2, my = LAY.meterY;
      ctx.fillStyle = 'rgba(255,255,255,0.14)';
      roundRect(mx, my, mw, 14, 7); ctx.fill();
      ctx.fillStyle = 'rgba(110,231,183,0.35)';   // the on-target band, shown honestly
      roundRect(mx + mw * S.POWER_MIN, my, mw * (S.POWER_MAX - S.POWER_MIN), 14, 7); ctx.fill();
      ctx.fillStyle = SO.power >= S.POWER_MIN && SO.power <= S.POWER_MAX ? acc : '#F87171';
      roundRect(mx + 2, my + 2, (mw - 4) * SO.power, 10, 5); ctx.fill();
    }
    if (SO.phase === 'result') {
      var msg = SO.outcome === 'goal' ? 'SCORED!'
        : SO.outcome === 'saved' ? 'SAVED!'
        : SO.outcome === 'short' ? 'TOO SOFT!'
        : SO.outcome === 'over' ? 'TOO STRONG!' : 'WIDE!';
      ctx.fillStyle = SO.outcome === 'goal' ? acc : '#F87171';
      ctx.font = 'bold 30px system-ui';
      ctx.fillText(msg, W / 2, H * 0.3);
    }
    ctx.textAlign = 'left';
  }

  // ---------- Map & end-card scenes ----------
  function drawStars(cx, cy, n, size){
    for (var i = 0; i < 3; i++) {
      ctx.fillStyle = i < n ? '#FCD34D' : 'rgba(255,255,255,0.18)';
      var sx = cx + (i - 1) * size * 1.5;
      ctx.save(); ctx.translate(sx, cy); ctx.beginPath();
      for (var k = 0; k < 5; k++) {
        var a = -Math.PI / 2 + k * (Math.PI * 2 / 5), a2 = a + Math.PI / 5;
        ctx.lineTo(Math.cos(a) * size, Math.sin(a) * size);
        ctx.lineTo(Math.cos(a2) * size * 0.45, Math.sin(a2) * size * 0.45);
      }
      ctx.closePath(); ctx.fill(); ctx.restore();
    }
  }
  function drawMap(){
    var sky = ctx.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, '#0F0A1A'); sky.addColorStop(1, '#1A1326');
    ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);

    // Landscape layout (AC-24): the stage is always wide, so each world is ONE ROW —
    // identity on the left, its six levels filling the rest. Stacking the dots under the title
    // (the old portrait layout) collided with it as soon as the panels got short.
    var headH = Math.min(58, H * 0.17);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#fff'; ctx.font = 'bold 20px system-ui';
    ctx.fillText('MONKEY WORLDS', W / 2, insetTop + 22);
    var titleHalf = ctx.measureText('MONKEY WORLDS').width / 2;
    drawMonkey(Math.min(W - 60, W / 2 + titleHalf + 24), insetTop + 30, 22, 1, 'idle', performance.now() / 16);
    ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.font = '11px system-ui';
    ctx.fillText('Pick a world — every world is open', W / 2, insetTop + 38);
    soundHit.x = W - 26; soundHit.y = insetTop + 14;
    ctx.font = '15px system-ui';
    ctx.fillText(audioOn ? '\uD83D\uDD0A' : '\uD83D\uDD07', W - 26, insetTop + 20);

    mapHits = [];
    var top0 = insetTop + headH;
    var avail = H - top0 - 10;
    var gap = 6;
    var panelH = Math.max(38, (avail - gap * 3) / 4);
    var labelW = Math.max(132, Math.min(210, W * 0.26));
    for (var w = 0; w < 4; w++) {
      var py = top0 + w * (panelH + gap);
      var wc = WORLDS[w];
      var grad = ctx.createLinearGradient(0, py, 0, py + panelH);
      grad.addColorStop(0, wc.skyGradient[1]); grad.addColorStop(1, '#140D22');
      ctx.fillStyle = grad;
      roundRect(12, py, W - 24, panelH, 14); ctx.fill();
      ctx.strokeStyle = wc.accent; ctx.globalAlpha = 0.55; ctx.lineWidth = 1.5;
      roundRect(12, py, W - 24, panelH, 14); ctx.stroke();
      ctx.globalAlpha = 1;

      var midY = py + panelH / 2;
      drawBall(w, 34, midY, 10, 0.4);
      ctx.textAlign = 'left';
      ctx.fillStyle = wc.accent; ctx.font = 'bold 13px system-ui';
      ctx.fillText(wc.name.toUpperCase(), 52, midY - 1);
      ctx.fillStyle = 'rgba(255,255,255,0.45)'; ctx.font = '10px system-ui';
      ctx.fillText(wc.action.label + ' \u00B7 6 levels', 52, midY + 13);

      var far = furthestSlot(w);
      var rowX = labelW, rowW = W - 24 - labelW;
      var dotW = rowW / 6;
      var dotH = Math.min(30, panelH - 14);
      var dy = midY - dotH / 2;
      for (var sl = 1; sl <= 6; sl++) {
        var dx = rowX + (sl - 1) * dotW;
        var unlocked = isLevelUnlockedInWorld(sl, far);
        var cleared = sl <= far;
        ctx.fillStyle = cleared ? wc.accent : unlocked ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)';
        roundRect(dx, dy, dotW - 7, dotH, 8); ctx.fill();
        ctx.textAlign = 'center';
        ctx.font = 'bold 12px system-ui';
        ctx.fillStyle = cleared ? '#0F0A1A' : unlocked ? '#fff' : 'rgba(255,255,255,0.35)';
        ctx.fillText(unlocked ? String(sl) : '\uD83D\uDD12', dx + (dotW - 7) / 2, dy + dotH / 2 + 4);
        var st = starFor(w, sl);
        if (st > 0) drawStars(dx + (dotW - 7) / 2, dy - 4, st, 3);
        ctx.textAlign = 'left';
        // hit box is the whole panel-height column, so a thumb never has to be precise
        mapHits.push({ x: dx, y: py, w: dotW - 7, h: panelH, world: w, slot: sl, locked: !unlocked });
      }
    }
    ctx.textAlign = 'left';
  }

  function drawEndCard(title, accent, showNext, nextLabel, subtitle){
    ctx.fillStyle = 'rgba(15,10,26,0.82)'; ctx.fillRect(0, 0, W, H);
    ctx.textAlign = 'center';
    ctx.fillStyle = accent; ctx.font = 'bold 30px system-ui';
    ctx.fillText(title, W / 2, H * 0.32);
    if (subtitle) {
      ctx.fillStyle = 'rgba(255,255,255,0.75)'; ctx.font = '15px system-ui';
      ctx.fillText(subtitle, W / 2, H * 0.32 + 30);
    }
    if (lastClear && typeof lastClear.stars === 'number' && scene !== 'failed') {
      drawStars(W / 2, H * 0.46, lastClear.stars, 16);
      ctx.fillStyle = '#FCD34D'; ctx.font = 'bold 16px system-ui';
      ctx.fillText(lastClear.coins + '/' + lastClear.coinTotal + ' coins', W / 2, H * 0.46 + 44);
    }
    if (showNext) {
      ctx.fillStyle = accent;
      roundRect(W / 2 - 110, H * 0.62, 220, 54, 27); ctx.fill();
      ctx.fillStyle = '#0F0A1A'; ctx.font = 'bold 17px system-ui';
      ctx.fillText(nextLabel, W / 2, H * 0.62 + 34);
    }
    ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.font = '14px system-ui';
    ctx.fillText('Tap anywhere for the map', W / 2, H * 0.62 + 92);
    ctx.textAlign = 'left';
  }

  // ---------- Main loop (fixed-timestep update under rAF, with a starvation watchdog) ----------
  var last = performance.now(), acc = 0, STEP = 1000 / 60;
  function tick(now){
    acc += Math.min(now - last, STEP * 5); // clamp huge gaps (backgrounded WebView)
    last = now;
    applyStageTransform();
    while (acc >= STEP) { drainPulses(); update(); updateShootout(); acc -= STEP; }
    if (scene === 'shootout' && game) drawShootout();
    else if (scene === 'play' && game) drawPlay();
    else if (scene === 'map') drawMap();
    else if (scene === 'clear') { if (game) drawPlay(); drawEndCard('LEVEL CLEAR!', WORLDS[lastClear.world].accent, true, 'NEXT LEVEL'); }
    else if (scene === 'failed') { if (game) drawPlay(); drawEndCard('WIPEOUT!', '#F87171', true, 'TRY AGAIN', 'No coins lost — go again'); }
    else if (scene === 'worldComplete') { if (game) drawPlay(); drawEndCard(WORLDS[lastClear.world].name.toUpperCase() + ' COMPLETE!', WORLDS[lastClear.world].accent, false, ''); }
    // Read-only state snapshot for QA/tuning harnesses (same spirit as window.__errs).
    // Track follows the scene. Polled rather than hooked into the ~8 scene assignments:
    // tick() runs from both rAF and the watchdog, so a switch is at worst a frame late (inaudible),
    // and any future scene assignment is covered for free. The world is part of the key because
    // advancing a level keeps the same scene string but must change arrangement.
    var musicKey = scene + ':' + (game ? game.world : '-');
    if (musicKey !== music.key) { music.key = musicKey; musicApplyScene(scene, game ? game.world : 0); }
    window.__music = (audioOn ? 'on' : 'off') + '|' + music.key + '|' + (ac ? ac.state : 'none')
      + '|n=' + music.scheduled + '|s=' + music.nextStep;
    window.__dbg = scene + (game ? '|' + game.player.x.toFixed(2) + ',' + game.player.y.toFixed(2) + ',' + game.player.hearts + ',' + game.coinsGot
      + ',' + (game.player.rope ? 'rope' : '-') + ',' + (game.ball ? 'ball' : '-') + ',' + (game.scored ? 'scored' : '-')
      + ',' + game.flag.x.toFixed(1) + ',shots=' + game.shots
      + ',camY=' + (game.camY || 0).toFixed(2) + ',tile=' + TILE : '')
      + (SO ? '|so:' + SO.phase + ',aim=' + SO.aim.toFixed(2) + ',pow=' + SO.power.toFixed(2)
        + ',keeper=' + SO.keeperShown.toFixed(2) + ',out=' + (SO.outcome || '-') + ',n=' + SO.shots : '');
  }
  function frame(now){
    tick(now);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
  // Some WebViews throttle rAF hard (low-power mode, partial occlusion) — a 60 Hz game becomes a
  // slideshow. If no rAF has run recently, pump the same tick from a timer so play stays smooth.
  setInterval(function(){
    var now = performance.now();
    if (now - last > 200) tick(now);
  }, 50);

  window.__post({ type: 'ready' });
  } catch (err) {
    try { window.__errs.push(String(err && err.message || err)); } catch (e2) {}
    window.__post({ type: 'load-error' });
  }
})();
</script>
</body>
</html>`;
}
